function opComponents () {
    class OpTabs extends HTMLElement {
        connectedCallback () {
            this.tabList = this.querySelector(`[role="tablist"]`);
            this.tabButtons = this.querySelectorAll(`[role="tab"]`);
            this.tabPanels = this.querySelectorAll(`[role="tabpanel"]`);
            this.underlineEl = this.querySelector("[data-tabs-underline]");

            this.tabButtons.forEach(tb => tb.addEventListener("click", this.tabBtn_clickHandler));

            if (this.tabButtons.length > 0) {
                this.selectTab(this.tabButtons[0]);
            }
        }

        disconnectedCallback () {
            this.tabButtons.forEach(tb => tb.removeEventListener("click", this.tabBtn_clickHandler));
        }

        tabBtn_clickHandler = (event) => {
            this.selectTab(event.currentTarget);
        }

        selectTab (selTab) {
            this.tabButtons.forEach(tb => {
                const isCurrent = tb === selTab;
                tb.ariaSelected = isCurrent;
                tb.toggleAttribute("data-current", isCurrent);

                if (isCurrent) {
                    this.showPanel(tb.getAttribute("data-tabs-target"));
                    this.updateUnderline(tb.offsetLeft, tb.offsetTop + tb.offsetHeight, tb.offsetWidth);
                }
            });
        }

        showPanel (selector) {
            const target = selector ? this.querySelector(selector) : null;

            this.tabPanels.forEach(tp => {
                tp.toggleAttribute("data-current", tp === target);
            });
        }

        updateUnderline (left, top, width) {
            if (this.underlineEl) {
                this.underlineEl.style.left = `${left}px`;
                this.underlineEl.style.top = `${top - this.underlineEl.offsetHeight}px`;
                this.underlineEl.style.width = `${width}px`;
            }
        }
    }

    class OpRangeSlider extends HTMLElement {
        connectedCallback () {
            this.inputMin = this.querySelector("input[data-slider-in='min']");
            this.inputMax = this.querySelector("input[data-slider-in='max']");
            this.track = this.querySelector("[data-slider-track]");
            this.range = this.querySelector("[data-slider-range]");
            this.labels = this.querySelectorAll("[data-slider-lb]");
            this.stepDisplay = this.querySelector("[data-step-display]");

            this.limitMin = Number(this.dataset.min) || 0;
            this.limitMax = Number(this.dataset.max) || 100;
            this.minRange = Number(this.dataset.minRange) || 0;
            this.stepGuides = Number(this.dataset.stepGuides) || 0;
            this.minVal = Number(this.inputMin?.value) || this.limitMin;
            this.maxVal = Number(this.inputMax?.value) || this.limitMax;

            if (this.inputMin) {
                this.inputMin.setAttribute("min", this.limitMin);
                this.inputMin.setAttribute("max", this.limitMax);
                this.inputMin.addEventListener("input", this.minInputHandler);
            }

            if (this.inputMax) {
                this.inputMax.setAttribute("min", this.limitMin);
                this.inputMax.setAttribute("max", this.limitMax);
                this.inputMax.addEventListener("input", this.maxInputHandler);
            }

            this.addEventListener("click", this.clickHandler);
            this.setCurrentAttr("min");
            this.updateStepDisplay();
            this.updateRange();
        }

        disconnectedCallback () {
            this.inputMin?.removeEventListener("input", this.minInputHandler);
            this.inputMax?.removeEventListener("input", this.maxInputHandler);
            this.removeEventListener("click", this.clickHandler);
        }

        clickHandler = (e) => {
            const currSlider = e.target.getAttribute("data-slider-in") || e.target.getAttribute("data-slider-lb");

            if (currSlider) {
                this.setCurrentAttr(currSlider);
            }
        }

        maxInputHandler = (e) => {
            const value = Math.max(Number(e.target.value), this.minVal + this.minRange);
            
            this.maxVal = value;
            e.target.value = value;

            this.setCurrentAttr("max");
            this.updateRange();
        }

        minInputHandler = (e) => {
            const value = Math.min(Number(e.target.value), this.maxVal - this.minRange);
            
            this.minVal = value;
            e.target.value = value;

            this.setCurrentAttr("min");
            this.updateRange();
        }

        getPercent (val) {
            return Math.round(((val - this.limitMin) / (this.limitMax - this.limitMin)) * 100);
        }

        setCurrentAttr (currentSlider) {
            this.inputMin?.toggleAttribute("data-current", currentSlider === "min");
            this.inputMax?.toggleAttribute("data-current", currentSlider === "max");

            this.labels.forEach(lb => {
                lb.toggleAttribute("data-current", lb.getAttribute("data-slider-lb") === currentSlider);
            });
        }

        updateRange () {
            const minPerc = this.getPercent(this.minVal);
            const maxPerc = this.getPercent(this.maxVal);

            if (this.range) {
                this.range.style.left = `${minPerc}%`;
                this.range.style.width = `${maxPerc - minPerc}%`;
            }

            this.labels.forEach(lb => {
                switch (lb.getAttribute("data-slider-lb")) {
                    case "min":
                        lb.style.left = `${minPerc}%`;
                        lb.textContent = this.minVal;
                        break;
                    case "max":
                        lb.style.left = `${maxPerc}%`;
                        lb.textContent = this.maxVal;
                        break;
                }
            });
        }

        updateStepDisplay () {
            const numStep = this.stepGuides;
            let stepHTML = "";

            if (!this.stepDisplay || !numStep) {
                return;
            }

            for (let i = 0; i <= numStep; i++) {
                const primary = i === 0 || i === numStep / 2 || i === numStep;
                stepHTML += `<span data-step-index="${i}" ${primary ? "data-step-primary" : ""}></span>`;
            }
            
            this.stepDisplay.innerHTML = stepHTML;
        }
    }

    class OpTextField extends HTMLElement {
        connectedCallback() {
            // Elements
            this.input = this.querySelector('input');
            this.textArea = this.querySelector('textarea');
            this.emptyField = this.querySelector('.js-empty-field');
            this.characterCounter = this.querySelector('.js-character-counter');
            this.dropdownOptions = this.querySelectorAll('.js-dropdown-option');
            this.showPickerBtns = this.querySelectorAll('.js-show-picker');
            this.showPassBtn = this.querySelector('.js-show-pass');

            // Icon wrappers (span) attesi per show/hide password
            this.iconEye = this.showPassBtn?.querySelector('.js-eye');
            this.iconEyeOff = this.showPassBtn?.querySelector('.js-eye-off');

            this.sanitizeShowPassMarkup();

            this.iconEye = this.showPassBtn?.querySelector('.js-eye');
            this.iconEyeOff = this.showPassBtn?.querySelector('.js-eye-off');

            this.input?.addEventListener('input', this.inputHandler);
            this.emptyField?.addEventListener('pointerdown', this.emptyFieldHandler);
            this.showPassBtn?.addEventListener('click', this.showPassBtnHandler);

            if (this.textArea && this.textArea.maxLength > 0) {
                this.textArea.addEventListener('input', this.textAreaHandler);
            }

            this.dropdownOptions.forEach((span) => {
                span.addEventListener('pointerdown', this.dropdownOptionHandler);
            });

            this.showPickerBtns.forEach((btn) => {
                btn.addEventListener('click', this.pickerBtnHandler);
            });

            this.syncPasswordIcons();
            this.inputHandler();
        }

        disconnectedCallback() {
            this.input?.removeEventListener('input', this.inputHandler);
            this.textArea?.removeEventListener('input', this.textAreaHandler);
            this.emptyField?.removeEventListener('pointerdown', this.emptyFieldHandler);
            this.showPassBtn?.removeEventListener('click', this.showPassBtnHandler);

            this.dropdownOptions.forEach((span) => {
                span.removeEventListener('pointerdown', this.dropdownOptionHandler);
            });

            this.showPickerBtns.forEach((btn) => {
                btn.removeEventListener('click', this.pickerBtnHandler);
            });
        }

        sanitizeShowPassMarkup = () => {
            if (!this.showPassBtn) return;

            let eyeWrapper = this.showPassBtn.querySelector('.js-eye');
            let eyeOffWrapper = this.showPassBtn.querySelector('.js-eye-off');

            if (!eyeWrapper) {
                eyeWrapper = document.createElement('span');
                eyeWrapper.className = 'js-eye';
                eyeWrapper.setAttribute('aria-hidden', 'true');
                this.showPassBtn.prepend(eyeWrapper);
            }
            if (!eyeOffWrapper) {
                eyeOffWrapper = document.createElement('span');
                eyeOffWrapper.className = 'js-eye-off tw-hidden';
                eyeOffWrapper.setAttribute('aria-hidden', 'true');
                this.showPassBtn.append(eyeOffWrapper);
            }


            const svgs = Array.from(this.showPassBtn.querySelectorAll('svg'));
            const isInWrapper = (svg) => svg.closest('.js-eye, .js-eye-off') !== null;

            let assignedEye = eyeWrapper.querySelector('svg');
            let assignedEyeOff = eyeOffWrapper.querySelector('svg');

            svgs.forEach((svg) => {
                if (isInWrapper(svg)) return;
                if (!assignedEye) {
                    eyeWrapper.appendChild(svg);
                    assignedEye = svg;
                    return;
                }
                if (!assignedEyeOff) {
                    eyeOffWrapper.appendChild(svg);
                    assignedEyeOff = svg;
                    return;
                }
                svg.remove();
            });


            assignedEye = eyeWrapper.querySelector('svg');
            assignedEyeOff = eyeOffWrapper.querySelector('svg');
            if (!assignedEye && assignedEyeOff) {
                eyeWrapper.appendChild(assignedEyeOff.cloneNode(true));
            } else if (!assignedEyeOff && assignedEye) {
                eyeOffWrapper.appendChild(assignedEye.cloneNode(true));
            }
        };

        inputHandler = () => {
            if (!this.emptyField) return;
            const hasValue = (this.input?.value?.length ?? 0) > 0;
            this.emptyField.classList.toggle('tw-hidden', !hasValue);
        };

        textAreaHandler = () => {
            if (!this.characterCounter || !this.textArea) return;
            this.characterCounter.textContent = this.textArea.value.length;
        };

        emptyFieldHandler = () => {
            if (!this.input) return;
            this.input.value = '';
            this.input.dispatchEvent(new Event('input', { bubbles: true }));
            this.emptyField?.classList.add('tw-hidden');
            this.input.dispatchEvent(new CustomEvent('empty', { bubbles: true }));
            this.syncPasswordIcons();
        };

        dropdownOptionHandler = (event) => {
            const value = event.target?.textContent;
            if (this.input && value != null) {
                this.input.value = value.trim();
                this.input.dispatchEvent(new Event('input', { bubbles: true }));
                this.inputHandler();
            }
        };

        pickerBtnHandler = () => {
            if (this.input && !this.input.disabled && !this.input.readOnly && typeof this.input.showPicker === 'function') {
                this.input.focus();
                this.input.showPicker();
            }
        };

        showPassBtnHandler = () => {
            if (!this.input) return;
            const willShow = this.input.type === 'password';
            this.input.type = willShow ? 'text' : 'password';
            this.syncPasswordIcons();
        };

        syncPasswordIcons = () => {
            const isVisible = this.input?.type === 'text';

            if (this.iconEye && this.iconEyeOff) {
                this.iconEye.classList.toggle('tw-hidden', !isVisible);
                this.iconEyeOff.classList.toggle('tw-hidden', isVisible);
            }

            // A11y sul bottone
            if (this.showPassBtn) {
                this.showPassBtn.setAttribute('aria-pressed', String(isVisible));
                this.showPassBtn.setAttribute('aria-label', isVisible ? 'Nascondi password' : 'Mostra password');
            }
        };
    }


    class OpSlideShow extends HTMLElement {
        connectedCallback () {
            const intervalSeconds = parseInt(this.dataset.interval) || 5;
            let currentIndex = 0;

            this.slides = this.querySelectorAll("[data-slide]");
            this.tooltip = this.querySelector("[data-ct-tooltip]");
            this.videoThumbnails = [...this.querySelectorAll("po-video-with-thumbnail")];

            this.interval = setInterval(() => {
                if (this.videoThumbnails.length === 0 || this.videoThumbnails.every(vt => vt.isPaused)) {
                    currentIndex = currentIndex < this.slides.length - 1 ? currentIndex + 1 : 0;
                    this.showSlide(currentIndex);
                }
            }, intervalSeconds * 1000);

            if (this.tooltip) {
                this.addEventListener("mousemove", this.mouseMoveHandler);
                this.addEventListener("mouseleave", this.mouseExitHandler);
            }

            this.showSlide(currentIndex);
        }

        disconnectedCallback () {
            clearInterval(this.interval);

            if (this.tooltip) {
                this.removeEventListener("mousemove", this.mouseMoveHandler);
                this.removeEventListener("mouseleave", this.mouseExitHandler);
            }
        }

        showSlide (showIndex) {
            for (let i = 0; i < this.slides.length; i++) {
                const isCurrent = i === showIndex;
                const slide = this.slides[i];
                
                slide.style.display = isCurrent ? "block" : "none";

                if (isCurrent) {
                    this.updateTooltip(slide.getAttribute("data-tooltip-text"));
                }
            }
        }

        updateTooltip (text = "") {
            const isEmpty = !text || text.trim() === "";

            if (this.tooltip) {
                this.tooltip.textContent = text;
                this.tooltip.toggleAttribute("data-visible", !isEmpty);
                
                if (isEmpty) {
                    this.tooltip.style.display = "none";
                }
            }
        }

        mouseMoveHandler = (event) => {
            if (this.tooltip?.hasAttribute("data-visible")) {
                this.tooltip.style.left = `${event.clientX + 20}px`;
                this.tooltip.style.top = `${event.clientY + 20}px`;
                this.tooltip.style.display = "block";
            }
        }

        mouseExitHandler = () => {
            if (this.tooltip) {
                this.tooltip.style.display = "none";
            }
        }
    }

    class PoCountdown extends HTMLElement {
        connectedCallback () {
            this.originalHTML = this.innerHTML;
            this.cdPlaceholder = this.dataset.cdPlaceholder || "%countdown%";

            this.endDate = this.parseDate(this.dataset.endTime);
            this.interval = setInterval(this.runCountdownInterval.bind(this), 1000);
            
            this.runCountdownInterval();
            this.classList.replace("tw-hidden", "tw-block");
        }

        disconnectedCallback () {
            clearInterval(this.interval);
        }

        displayError (message) {
            if (window.Shopify?.designMode) {
                this.innerHTML = this.originalHTML.replace(this.cdPlaceholder, message);
            }

            console.error("Countdown:", message);
        }

        formatTime(time) {
            const hours = Math.floor(time / (1000 * 60 * 60));
            const minutes = Math.floor((time % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((time % (1000 * 60)) / 1000);
            const timeParts = [];
            if (hours > 0) {
                timeParts.push(`${String(hours).padStart(2, '0')} H`);
            }
            if (hours > 0 || minutes > 0) {
                timeParts.push(`${String(minutes).padStart(2, '0')} M`);
            }
            if (hours > 0 || minutes > 0 || seconds > 0) {
                timeParts.push(`${String(seconds).padStart(2, '0')} S`);
            }

            if (timeParts.length === 0) {
                clearInterval(this.interval);
                return '';
            }
            return timeParts.join(' : ');
        }        

        parseDate (rawDate = "") {
            const dateSetting = rawDate?.split(" ");
            let dateString = dateSetting ? dateSetting[0] : null;

            if (!dateString || dateString.trim() === "") {
                this.displayError("No date provided!");
                return -1;
            }

            if (dateSetting.length > 1) {
                dateString += "T" + dateSetting[1];
            }

            const parsedDate = new Date(dateString).getTime();

            if (isNaN(parsedDate)) {
                this.displayError("Invalid date!");
                return -1;
            }

            return parsedDate;
        }

        runCountdownInterval () {
            if (this.endDate !== -1 && this.originalHTML.includes(this.cdPlaceholder)) {
                this.updateCountdown();
            }
        }

        updateCountdown() {
            const now = Date.now();
            const timeRemaining = this.endDate - now;
            
            if (timeRemaining <= 0) {
                this.innerHTML = this.originalHTML.replace(this.cdPlaceholder, '');
                clearInterval(this.interval);
                return;
            }
            const formattedTime = this.formatTime(Math.max(0, timeRemaining));
            this.innerHTML = this.originalHTML.replace(this.cdPlaceholder, formattedTime);
        }
    }

    class OpQuantitySelector extends HTMLElement {
        connectedCallback() {
            this.input = this.querySelector("input");
            this.quantityAdd = this.querySelector("[data-quantity-add]");
            this.quantitySub = this.querySelector("[data-quantity-sub]");
            this.quantityAdd?.addEventListener("click", this.quantityAddHandler);
            this.quantitySub?.addEventListener("click", this.quantitySubHandler);
        }

        quantityAddHandler = () => {
            this.input.value = Math.max(0, Number(this.input.value) + 1);
        }

        quantitySubHandler = () => {
            this.input.value = Math.max(0, Number(this.input.value) - 1);
        }
    }

    class OpPasswordChecker extends HTMLElement {
        connectedCallback() {
            this.input = document.querySelector(this.dataset.input);
            this.inputConfirm = document.querySelector(this.dataset.inputConfirm);
            this.clearButton = this.input.closest("po-text-field")?.querySelector(".js-empty-field");
            this.lengthOutput = this.querySelector("[data-password-checker='length']");
            this.uppercaseOutput = this.querySelector("[data-password-checker='uppercase']");
            this.lowercaseOutput = this.querySelector("[data-password-checker='lowercase']");
            this.numberOutput = this.querySelector("[data-password-checker='number']");
            this.specialOutput = this.querySelector("[data-password-checker='special']");
            this.passwordVerified = false;
            this.waitForInputs = false;
            this.requirements = {
                length: false,
                uppercase: false,
                lowercase: false,
                number: false,
                special: false,
                passwordConfirmed: false,
            }

            this.input?.addEventListener("input", this.checkPassword.bind(this));
            this.inputConfirm?.addEventListener("input", this.checkPassword.bind(this));
            this.clearButton?.addEventListener("pointerdown", this.checkPassword.bind(this));
        }

        checkPassword() {
            this.lengthCheck();
            this.uppercaseCheck();
            this.lowercaseCheck();
            this.numberCheck();
            this.specialCheck();
            this.checkConfirmPassword();
            this.toggleSubmitButton();
        }

        toggleSubmitButton() {
            const submitButton = this.closest("form").querySelector("[data-submit-form]");
            const isDisabled = Object.values(this.requirements).includes(false);
            this.passwordVerified = !isDisabled;
            if (!this.waitForInputs) submitButton.disabled = isDisabled;
        }

        lengthCheck() {
            const condition = this.input.value.length >= 8;
            this.checkCondition(condition, "length", this.lengthOutput);
        }

        uppercaseCheck() {
            const condition = /[A-Z]/.test(this.input.value);
            this.checkCondition(condition, "uppercase", this.uppercaseOutput);
        }

        lowercaseCheck() {
            const condition = /[a-z]/.test(this.input.value);
            this.checkCondition(condition, "lowercase", this.lowercaseOutput);
        }

        numberCheck() {
            const condition = /[0-9]/.test(this.input.value);
            this.checkCondition(condition, "number", this.numberOutput);
        }

        specialCheck() {
            const condition = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(this.input.value);
            this.checkCondition(condition, "special", this.specialOutput);
        }

        checkCondition(condition, key, output) {
            const verifCheck = output?.querySelector(".verification-check");
            const verifNeutral = output?.querySelector(".verification-neutral");

            if (condition) {
                this.requirements[key] = true;
                verifCheck?.classList.remove("tw-hidden");
                verifNeutral?.classList.add("tw-hidden");
            } else {
                this.requirements[key] = false;
                verifCheck?.classList.add("tw-hidden");
                verifNeutral?.classList.remove("tw-hidden");
            }
        }

        checkConfirmPassword() {
            if (!this.inputConfirm) return;

            const passConfirmed = this.input.value === this.inputConfirm.value;

            this.checkCondition(passConfirmed, "passwordConfirmed");

            if (this.input.value && this.inputConfirm.value) {
                this.inputConfirm.closest("po-text-field")?.classList.toggle("tw-po-textfield-error", !passConfirmed);
            }
        }
    }

    class OpPasswordCheckerContainer extends HTMLElement {
        constructor() {
          super();
          this.checkerContainer = this.getElementsByClassName("password-checker-container")[0];
          this.passwordInput = document.getElementById(this.dataset.customerPassword);
          
          this.init();
        }
      
        init() {
          this.passwordInput.addEventListener("input", () => this.toggleVisibility());
        }
      
        toggleVisibility() {
            if (this.passwordInput.value.length > 0) {
              this.checkerContainer.classList.remove("tw-hidden");
            } else {
              this.checkerContainer.classList.add("tw-hidden");
            }
        }
      
        disconnectedCallback() {
          this.passwordInput.removeEventListener("input", this.toggleVisibility);
        }
      }

    class OpForm extends HTMLElement {
        connectedCallback() {
            this.form = this.querySelector("form");
            this.submitButton = this.form.querySelector("[data-submit-form]");
            this.passwordChecker = this.form.querySelector("op-password-checker");
            this.allInputsFilled = false;

            if (!this.form || !this.submitButton) return;
            
            this.form.addEventListener("input", this.checkRequiredFields.bind(this));
        }
        
        checkRequiredFields() {
            this.allInputsFilled = Array.from(this.form.querySelectorAll('input[required]')).every(input => input.value.trim() !== '');
            this.toggleSubmitButton();
        }
        
        toggleSubmitButton() {
            if (this.passwordChecker) {
                this.submitButton.disabled = !(this.passwordChecker.passwordVerified && this.allInputsFilled);
            } else {
                this.submitButton.disabled = !this.allInputsFilled;
            }
        }
    }

    class OpVideoWithThumbnail extends HTMLElement {
        get isPaused () {
            return this.videoElement?.paused || this.dataset.playing === "false";
        }

        getActiveVideoElement() {
            const videos = [...this.querySelectorAll('video')];
            if (!videos.length) return null;

            const visibleVideo = videos.find((video) => {
                const styles = window.getComputedStyle(video);
                return styles.display !== 'none' && styles.visibility !== 'hidden';
            });

            return visibleVideo || videos[0];
        }

        setActiveVideoElement() {
            const nextVideoElement = this.getActiveVideoElement();
            if (!nextVideoElement || nextVideoElement === this.videoElement) return;

            if (this.videoElement) {
                this.videoElement.removeEventListener("play", this._onVideoPlayPause);
                this.videoElement.removeEventListener("pause", this._onVideoPlayPause);
            }

            this.videoElement = nextVideoElement;
            this.videoElement.addEventListener("play", this._onVideoPlayPause);
            this.videoElement.addEventListener("pause", this._onVideoPlayPause);
            this.updatePlayPauseIcons();
        }

        connectedCallback() {
            this._onVideoPlayPause = this.updatePlayPauseIcons.bind(this);
            this._onResize = this.setActiveVideoElement.bind(this);

            this.videoElement = this.getActiveVideoElement();
            this.playPauseBtn = this.querySelector("[data-play-btn]");
            this.audioMuteBtn = this.querySelector("[data-mute-btn]");

            if (!this.videoElement || !this.playPauseBtn) return;

            this.playPauseBtn.addEventListener("click", this.togglePlayPause.bind(this));
            if (this.audioMuteBtn) {
                this.audioMuteBtn.addEventListener("click", this.toggleMute.bind(this));
            }

            this.videoElement.addEventListener("play", this._onVideoPlayPause);
            this.videoElement.addEventListener("pause", this._onVideoPlayPause);
            this.updatePlayPauseIcons();
            window.addEventListener("resize", this._onResize);

            this.addEventListener('mouseleave', () => {
                if (this.dataset.playing === "true") {
                  this.dataset.playing = "hover";
                }
            });

            if (this.dataset.playing === "autoplay") {
                this.setupAutoplay();
            }
        }

        disconnectedCallback() {
            window.removeEventListener("resize", this._onResize);
            if (this.videoElement && this._onVideoPlayPause) {
                this.videoElement.removeEventListener("play", this._onVideoPlayPause);
                this.videoElement.removeEventListener("pause", this._onVideoPlayPause);
            }
        }

        updatePlayPauseIcons() {
            const playEl = this.querySelector(".tw-play-btn");
            const pauseEl = this.querySelector(".tw-pause-btn");
            const isPlaying = this.videoElement && !this.videoElement.paused;
            if (playEl) playEl.classList.toggle("tw-hidden", isPlaying);
            if (pauseEl) pauseEl.classList.toggle("tw-hidden", !isPlaying);
        }

        setupAutoplay() {
            this.setActiveVideoElement();
            if (!this.videoElement) return;

            // Pre-mute only when explicitly requested; otherwise we'll try audio first in startAutoplay
            if (this.dataset.muted !== 'false') {
                this.videoElement.muted = true;
            }
            this.videoElement.setAttribute('playsinline', '');
            this.videoElement.setAttribute('webkit-playsinline', '');
            this.videoElement.load();
            
            // Use IntersectionObserver to trigger autoplay when visible (better iOS support)
            this.autoplayObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.startAutoplay();
                        this.autoplayObserver?.disconnect();
                    }
                });
            }, { threshold: 0.1 });
            
            this.autoplayObserver.observe(this);
        }

        startAutoplay() {
            this.setActiveVideoElement();
            if (!this.videoElement) return;

            // Respect data-muted: if false, try playing with audio first
            const wantsMuted = this.dataset.muted !== 'false';

            /**
             * @param {boolean} muted - whether to attempt play in muted mode
             */
            const attemptPlay = (muted) => {
                this.videoElement.muted = muted;
                // Make sure video is visible before playing (iOS requirement)
                this.videoElement.classList.remove('tw-hidden');
                this.videoElement.style.opacity = '1';
                
                const playPromise = this.videoElement.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            this.dataset.playing = "true";
                            const thumbnail = this.querySelector('.tw-po-thumbnail-image');
                            if (thumbnail) thumbnail.classList.add('tw-hidden');
                            this.updatePlayPauseIcons();
                            // Sync mute icon with the actual muted state after play
                            this._syncMuteIcons(this.videoElement.muted);
                        })
                        .catch(() => {
                            if (!muted) {
                                // Browser blocked unmuted autoplay — fall back silently to muted
                                this._syncMuteIcons(true);
                                attemptPlay(true);
                            } else {
                                // Autoplay fully prevented (iOS Low Power Mode etc.)
                                // Show thumbnail and let the user click to play
                                this.dataset.playing = "false";
                                this.videoElement.classList.add('tw-hidden');
                                const thumbnail = this.querySelector('.tw-po-thumbnail-image');
                                if (thumbnail) thumbnail.classList.remove('tw-hidden');
                            }
                        });
                }
            };

            // iOS needs the video to have some data loaded
            if (this.videoElement.readyState >= 1) {
                attemptPlay(wantsMuted);
            } else {
                this.videoElement.addEventListener('loadedmetadata', () => attemptPlay(wantsMuted), { once: true });
                // Also try on canplay for slower connections
                this.videoElement.addEventListener('canplay', () => attemptPlay(wantsMuted), { once: true });
            }
        }

        /**
         * Syncs the audio/mute icon visibility to the given muted state.
         * @param {boolean} muted
         */
        _syncMuteIcons(muted) {
            const audioBtn = this.querySelector('[data-audio-btn]');
            const muteBtn = this.querySelector('[data-mute-btn].tw-mute-btn');
            if (audioBtn) audioBtn.classList.toggle('tw-hidden', muted);
            if (muteBtn) muteBtn.classList.toggle('tw-hidden', !muted);
            this.dataset.muted = String(muted);
        }

        togglePlayPause() {
            this.setActiveVideoElement();
            if (this.videoElement.paused) {
                if (this.videoElement.src || this.videoElement.querySelector('source')) {
                    this.videoElement.play().catch(() => {});
                }
                this.dataset.playing = "true";
                this.videoElement.classList.remove('tw-hidden');
                const thumbnail = this.querySelector('.tw-po-thumbnail-image');
                if (thumbnail) thumbnail.classList.add('tw-hidden');
            } else {
                this.videoElement.pause();
                this.dataset.playing = "false";
            }
            this.updatePlayPauseIcons();
        }

        toggleMute() {
            this.setActiveVideoElement();
            const audioBtn = this.querySelector('[data-audio-btn]');
            const muteBtn = this.querySelector('[data-mute-btn].tw-mute-btn');
          
            if (!audioBtn || !muteBtn) return;
          
            if (this.videoElement.muted) {
              this.videoElement.muted = false;
              this.dataset.muted = false;
          
              if (this.dataset.playing === "hover") {
                this.dataset.playing = true;
              }
          
              audioBtn.classList.remove('tw-hidden');
              muteBtn.classList.add('tw-hidden');
            } else {
              this.videoElement.muted = true;
              this.dataset.muted = true;
          
              audioBtn.classList.add('tw-hidden');
              muteBtn.classList.remove('tw-hidden');
            }
        }
    }

    class OpModalTrigger extends HTMLElement {
        connectedCallback() {
            this.modal = document.querySelectorAll(`#${this.dataset.modalId}`);
            this.trigger = this.querySelector(".js-modal-trigger") || this.querySelector("button");
            
            this.animation = this.hasAttribute("data-animate");
            this.animateBody = this.dataset.animateBody === "true";
            
            this.trigger?.addEventListener("click", this.triggerClickHandler);

            if (this.animateBody) {
                document.body.classList.add("tw-transition-transform");
                document.body.classList.add("tw-duration-300");
            }
            
            if (this.animation) {
                this.modal.forEach(element => {
                    element?.addEventListener('close', this.modalCloseHandler);
                })
            }
        }

        disconnectedCallback() {
            this.trigger?.removeEventListener("click", this.triggerClickHandler);

            if (this.animateBody) {
                document.body.classList.remove("tw-transition-transform");
                document.body.classList.remove("tw-duration-300");
            }

            if (this.animation) {
                this.modal.forEach(element => {
                    element?.removeEventListener('close', this.modalCloseHandler);
                })
            }
        }

        triggerClickHandler = () => {     
            this.modal.forEach(element => {
                element?.showModal();
            });       

            if (!this.animation) return;

            this.modal.forEach(element => {
                element?.classList.remove("tw-translate-x-full")
            })
            if (this.animateBody) document.body.style.overflowY = "hidden";
        }

        modalCloseHandler = () => {
            this.modal.forEach(element => {
                element?.classList.add("tw-translate-x-full")
            })
            if (this.animateBody) document.body.style.overflowY = "auto";

        }
    }

    class PoModalCloser extends HTMLElement {
        connectedCallback() {
            this.modal = this.closest("dialog");
            this.backdropClickClose = this.dataset.backdropClose === "true";
            
            this.addEventListener("click", this.closeModal);

            if (this.backdropClickClose) {
                this.modal?.addEventListener("click", this.backdropClickHandler);   
            }
        }

        backdropClickHandler = (event) => {
            if (event.target === this.modal) {
                this.closeModal();
            }
        }

        closeModal = () => {
            this.modal?.close();
        }

        disconnectedCallback() {
            this.removeEventListener("click", this.closeModal);
            
            if (this.backdropClickClose) {
                this.modal?.removeEventListener("click", this.backdropClickHandler);
            }
        }
    }

    class PoSliderComponent extends HTMLElement {
        constructor() {
            super();
            this.slider = this.querySelector('[id^="Slider-"]');
            this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
            this.enableSliderLooping = true; // Abilitato il loop infinito
            this.currentPageElement = this.querySelector('.slider-counter--current');
            this.currentPageElementMobile = this.querySelector('.slider-counter-current--mobile');
            this.pageTotalElements = this.querySelectorAll('.slider-counter--total');
            this.pageTotalElement = this.querySelector('.slider-counter--total');
            this.prevButton = this.querySelector('button[name="previous"]');
            this.nextButton = this.querySelector('button[name="next"]');
            this.prevButtonMobile = this.querySelector('button[name="previous_mobile"]');
            this.nextButtonMobile = this.querySelector('button[name="next_mobile"]');
            this.slidesPerView = Number(this.dataset.slidesPerView);
            this.thumbnailWrapper = this.querySelector(".slider-thumbnails");
            this.thumbnailItems = this.querySelectorAll('[id^="Thumbnail-"]');
            this.thumbnailDragging = false;
            this.productAnchor = this.closest(".po-algolia-product-card").querySelector(".tile-title > a");
            this.deferSliderInit = this.dataset.deferSliderInit === "true";
            this.sliderInitialized = false;
            this.deferredInitObserver = null;
            this.activityObserver = null;
            this.sliderInteractionsActive = false;

            this.isScrolling = false;
            this.scrollTimeout = null;
            this.lastScrollLeft = 0;
            this.isDragging = false;
            this.dragStartX = 0;
            this.isMouseDown = false;
            this.isLoopTransitioning = false;
            this.loopTransitionTimeout = null;
            this.pendingLoopPage = null;
            this.savedInlineScrollBehavior = '';
            this.savedInlineScrollSnapType = '';
            this.savedInlineOverflowAnchor = '';
            
            this.dragStartTime = 0;
            this.totalDragDistance = 0;
            this.hasSwipedRecently = false;
            this.swipeThreshold = 10;
            this.clickPreventionDuration = 200; 
            this.ignoreClickPreventionUntil = 0;
            this.skipNextClickPrevention = false;
            this.suppressNextClickUntil = 0;
            this.arrowClickAt = 0;
            this.dragStartY = 0;
            this.totalDragDistanceY = 0;
            this.lastAppliedVariantId = null;
            this.plpSlideSyncTimeout = null;
            this.plpInfoRequestId = 0;
            this.lastPlpSyncedHandle = null;
            this.plpNavigationTargetIndex = null;
            this.plpNavigationSyncFallbackTimeout = null;
            const productCard = this.closest(".po-algolia-product-card");
            this.productAnchor = productCard?.querySelector(".tile-title > a");
            this.resizeObserver = null;
            this.pointerScrollCleanup = null;
            this.clickableElements = [];
            this.boundUpdate = this.update.bind(this);
            this.boundOnDragStart = this.onDragStart.bind(this);
            this.boundOnDragMove = this.onDragMove.bind(this);
            this.boundOnDragEnd = this.onDragEnd.bind(this);
            this.boundOnMouseDown = this.onMouseDown.bind(this);
            this.boundOnMouseMove = this.onMouseMove.bind(this);
            this.boundOnMouseUp = this.onMouseUp.bind(this);
            this.boundOnButtonClick = this.onButtonClick.bind(this);
            this.boundThumbnailClick = (event) => this.onThumbnailClick(event, event.currentTarget);
            this.boundResize = () => this.initPages();
            this.boundClickPrevention = this.handleClickPrevention.bind(this);
            this.boundDeferredInit = this.initializeDeferredSlider.bind(this);

            this.initImageSkeleton();
            this.seedInitialPlpInfoCache();

            if (!this.slider || !this.nextButton) return;

            if (this.thumbnailItems.length < 2) {
                this.prevButton?.toggleAttribute("disabled", true);
                this.nextButton?.toggleAttribute("disabled", true);
                this.prevButtonMobile?.toggleAttribute("disabled", true);
                this.nextButtonMobile?.toggleAttribute("disabled", true);
                return
            }

            if (this.deferSliderInit) {
                this.setupDeferredSliderInit();
                return;
            }

            this.initializeSlider();
        }

        disconnectedCallback() {
            this.teardownDeferredSliderInit();
            this.teardownActivityObserver();
            this.deactivateSliderInteractions();
        }

        setupDeferredSliderInit() {
            if (this.sliderInitialized) {
                return;
            }

            this.addEventListener('mouseenter', this.boundDeferredInit);
            this.addEventListener('focusin', this.boundDeferredInit);
            this.addEventListener('touchstart', this.boundDeferredInit, { passive: true });

            if (!('IntersectionObserver' in window)) {
                this.initializeDeferredSlider();
                return;
            }

            this.deferredInitObserver = new IntersectionObserver((entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    this.initializeDeferredSlider();
                }
            }, { rootMargin: '200px 0px' });

            this.deferredInitObserver.observe(this);
        }

        teardownDeferredSliderInit() {
            this.removeEventListener('mouseenter', this.boundDeferredInit);
            this.removeEventListener('focusin', this.boundDeferredInit);
            this.removeEventListener('touchstart', this.boundDeferredInit);
            this.deferredInitObserver?.disconnect();
            this.deferredInitObserver = null;
        }

        initializeDeferredSlider() {
            this.teardownDeferredSliderInit();
            this.initializeSlider();
        }

        initializeSlider() {
            if (this.sliderInitialized || !this.slider) {
                return;
            }

            this.sliderInitialized = true;
            this.initPages();
            this.setupActivityObserver();
        }

        addClickPreventionListeners() {
            this.clickableElements = Array.from(
                this.slider.querySelectorAll('a, button:not([name="previous"]):not([name="next"])')
            );

            this.clickableElements.forEach((element) => {
                element.addEventListener('click', this.boundClickPrevention, true);
            });
        }

        removeClickPreventionListeners() {
            this.clickableElements.forEach((element) => {
                element.removeEventListener('click', this.boundClickPrevention, true);
            });
            this.clickableElements = [];
        }

        handleClickPrevention(event) {
            if (this.suppressNextClickUntil && Date.now() < this.suppressNextClickUntil) {
                this.suppressNextClickUntil = 0;
                event.preventDefault();
                event.stopPropagation();
                return false;
            }
            if (this.skipNextClickPrevention) {
                this.skipNextClickPrevention = false;
                this.hasSwipedRecently = false;
                return;
            }
            if (this.ignoreClickPreventionUntil && Date.now() < this.ignoreClickPreventionUntil) {
                this.ignoreClickPreventionUntil = 0;
                return;
            }
            if (this.hasSwipedRecently) {
                event.preventDefault();
                event.stopPropagation();
                return false;
            }
        }

        initImageSkeleton() {
            const imageWrappers = this.querySelectorAll('.po-card-image[data-image-ready]');
            const cardWrapper = this.closest('.tw-po-tile[data-card-ready]');
            const firstImageWrapper = imageWrappers[0];

            const markCardReady = () => {
                if (cardWrapper) {
                    cardWrapper.dataset.cardReady = "true";
                }
            };

            imageWrappers.forEach((wrapper) => {
                const mainImage = wrapper.querySelector('.img-main img');
                const isFirstWrapper = wrapper === firstImageWrapper;

                if (!mainImage) {
                    wrapper.dataset.imageReady = "true";
                    if (isFirstWrapper) {
                        markCardReady();
                    }
                    return;
                }

                const markReady = () => {
                    wrapper.dataset.imageReady = "true";
                    if (isFirstWrapper) {
                        markCardReady();
                    }
                };

                if (mainImage.complete && mainImage.naturalWidth > 0) {
                    markReady();
                    return;
                }

                mainImage.addEventListener('load', markReady, { once: true });
                mainImage.addEventListener('error', markReady, { once: true });
            });
        }

        initPages() {
            this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
            if (this.sliderItemsToShow.length < 1) return;

            if (this.sliderItemsToShow.length > 1) {
                this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
            } else {
                this.sliderItemOffset = this.sliderItemsToShow[0].clientWidth || this.slider.clientWidth || 1;
            }

            if (!this.sliderItemOffset || this.sliderItemOffset < 1) {
                this.sliderItemOffset = this.sliderItemsToShow[0].clientWidth || this.slider.clientWidth || 1;
            }

            const slidesPerPage = this.slidesPerView > 0
                ? this.slidesPerView
                : Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);

            this.slidesPerPage = Math.max(1, Math.floor(slidesPerPage) || 1);
            this.totalPages = Math.max(1, this.sliderItemsToShow.length - this.slidesPerPage + 1);
            this.update();
        }

        getClosestSlideIndex(scrollLeft = this.slider?.scrollLeft || 0) {
            if (!this.sliderItemsToShow || this.sliderItemsToShow.length === 0) {
                return 0;
            }

            let closestIndex = 0;
            let closestDistance = Number.POSITIVE_INFINITY;

            this.sliderItemsToShow.forEach((slide, index) => {
                const distance = Math.abs(slide.offsetLeft - scrollLeft);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });

            return closestIndex;
        }

        getSlidePositionByIndex(index) {
            if (!this.sliderItemsToShow || this.sliderItemsToShow.length === 0) {
                return 0;
            }

            const safeIndex = Math.min(Math.max(index, 0), this.sliderItemsToShow.length - 1);
            return this.sliderItemsToShow[safeIndex]?.offsetLeft || 0;
        }

        getMaxScrollLeft() {
            if (!this.slider) return 0;
            return Math.max(this.slider.scrollWidth - this.slider.clientWidth, 0);
        }

        resetPages() {
            this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
            this.initPages();
        }

        static responseCache = new Map();
        static responseCacheLimit = 80;
        static visibilityComponents = new WeakMap();
        static visibilityObserver = null;

        static getVisibilityObserver() {
            if (PoSliderComponent.visibilityObserver || !('IntersectionObserver' in window)) {
                return PoSliderComponent.visibilityObserver;
            }

            PoSliderComponent.visibilityObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    const component = PoSliderComponent.visibilityComponents.get(entry.target);
                    if (!component) {
                        return;
                    }

                    if (entry.isIntersecting) {
                        component.activateSliderInteractions();
                    } else {
                        component.deactivateSliderInteractions();
                    }
                });
            }, {
                rootMargin: '350px 0px',
                threshold: 0.01,
            });

            return PoSliderComponent.visibilityObserver;
        }

        static setResponseCache(cacheKey, value) {
            if (PoSliderComponent.responseCache.has(cacheKey)) {
                PoSliderComponent.responseCache.delete(cacheKey);
            }

            PoSliderComponent.responseCache.set(cacheKey, value);

            while (PoSliderComponent.responseCache.size > PoSliderComponent.responseCacheLimit) {
                const oldestKey = PoSliderComponent.responseCache.keys().next().value;
                if (oldestKey === undefined) break;
                PoSliderComponent.responseCache.delete(oldestKey);
            }
        }

        /**
         * Syncs OOS image dimming after PLP status updates (swatch swipe / metafields fetch).
         * @param {Element|null} container
         */
        static getPlpCardRoot(el) {
            return el?.closest?.('.po-algolia-product-card')
                || el?.closest?.('.ais-Hits-item')
                || el?.closest?.('po-dynamic-product-card');
        }

        static syncPlpCardOosOpacity(container, statusText) {
            if (!container?.querySelector) return;

            const oosTarget = container.querySelector('[data-plp-oos-target]');
            if (!oosTarget) return;

            const resolvedStatus = statusText ?? container.querySelector('[data-prod-status]')?.textContent?.trim() ?? '';
            oosTarget.classList.toggle('tw-opacity-50', resolvedStatus.length > 0);
        }

        /**
         * Updates PLP OOS label and image opacity for the active color swatch.
         * @param {Element|null} container
         * @param {string} statusText
         */
        static applyPlpProdStatus(container, statusText = '') {
            if (!container?.querySelector) return;

            const statusEl = container.querySelector('[data-prod-status]');
            if (statusEl) {
                statusEl.textContent = statusText;
            }

            PoSliderComponent.syncPlpCardOosOpacity(container, statusText);
        }

        isNearViewport() {
            const rect = this.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;

            return (
                rect.width > 0 &&
                rect.height > 0 &&
                rect.bottom >= -200 &&
                rect.right >= 0 &&
                rect.top <= viewportHeight + 350 &&
                rect.left <= viewportWidth
            );
        }

        setupActivityObserver() {
            if (this.activityObserver) {
                return;
            }

            const observer = PoSliderComponent.getVisibilityObserver();
            if (!observer) {
                this.activateSliderInteractions();
                return;
            }

            this.activityObserver = observer;
            PoSliderComponent.visibilityComponents.set(this, this);
            observer.observe(this);

            if (this.isNearViewport()) {
                this.activateSliderInteractions();
            }
        }

        teardownActivityObserver() {
            if (!this.activityObserver) {
                return;
            }

            this.activityObserver.unobserve(this);
            this.activityObserver = null;
            PoSliderComponent.visibilityComponents.delete(this);
        }

        activateSliderInteractions() {
            if (this.sliderInteractionsActive || !this.slider) {
                return;
            }

            this.sliderInteractionsActive = true;
            this.initPages();
            this.resizeObserver = new ResizeObserver(this.boundResize);
            this.resizeObserver.observe(this.slider);

            this.slider.addEventListener('scroll', this.boundUpdate);
            this.slider.addEventListener('touchstart', this.boundOnDragStart, { passive: false });
            this.slider.addEventListener('touchmove', this.boundOnDragMove, { passive: false });
            this.slider.addEventListener('touchend', this.boundOnDragEnd, { passive: false });
            this.slider.addEventListener('mousedown', this.boundOnMouseDown);
            this.slider.addEventListener('mousemove', this.boundOnMouseMove);
            this.slider.addEventListener('mouseup', this.boundOnMouseUp);
            this.slider.addEventListener('mouseleave', this.boundOnMouseUp);

            this.prevButton?.addEventListener('click', this.boundOnButtonClick);
            this.nextButton?.addEventListener('click', this.boundOnButtonClick);
            this.prevButtonMobile?.addEventListener('click', this.boundOnButtonClick);
            this.nextButtonMobile?.addEventListener('click', this.boundOnButtonClick);

            this.addClickPreventionListeners();

            if (this.thumbnailWrapper && this.thumbnailItems.length > 0) {
                this.pointerScrollCleanup = this.pointerScroll(this.thumbnailWrapper);
                this.sliderItems = this.querySelectorAll('[id^="Slide-"]:not([data-po-hidden])');
                this.thumbnailItems = this.querySelectorAll('[id^="Thumbnail-"]:not([data-po-hidden])');
                this.initPages();

                if (this.closest(".po-algolia-product-card")) {
                    this.syncThumbnailSelectionToVisibleSlide();
                } else {
                    const hitThumbnail = this.getThumbnailForPlpHit();
                    this.thumbnailItems.forEach((thumb) => {
                        thumb.dataset.selected = thumb === hitThumbnail ? "true" : "false";
                    });
                }
            }

            if (this.thumbnailItems.length > 0) {
                this.thumbnailItems.forEach((thumbnail) => {
                    thumbnail.addEventListener("click", this.boundThumbnailClick);
                });
            }

            this.syncPlpHitSelectionWithoutScroll();
        }

        /**
         * Returns true when the slider is owned by po-plp-product-card (Algolia PLP).
         * @returns {boolean}
         */
        isPlpProductCardSlider() {
            return Boolean(this.closest("po-plp-product-card"));
        }

        /**
         * Keeps PLP thumbnail selection aligned with the currently visible slide.
         * @returns {Element|null}
         */
        syncThumbnailSelectionToVisibleSlide() {
            if (!this.thumbnailItems?.length) {
                return null;
            }

            const slideIndex = this.getClosestSlideIndex();
            const slide = this.sliderItemsToShow?.[slideIndex] || null;
            const currentThumbnail = this.getThumbnailForSlide(slide)
                || this.thumbnailItems[slideIndex]
                || this.thumbnailItems[0]
                || null;

            if (!currentThumbnail) {
                return null;
            }

            this.thumbnailItems.forEach((thumb) => {
                thumb.dataset.selected = thumb === currentThumbnail ? "true" : "false";
            });

            this.currentPage = slideIndex + 1;
            this.currentHandle = currentThumbnail.dataset?.currentVariant
                || slide?.dataset?.variantHandle
                || "";
            this.updateCurrentPageDisplay(this.currentPage);

            return currentThumbnail;
        }

        deactivateSliderInteractions() {
            if (!this.sliderInteractionsActive) {
                return;
            }

            this.sliderInteractionsActive = false;
            clearTimeout(this.plpSlideSyncTimeout);
            this.plpSlideSyncTimeout = null;
            this.plpNavigationTargetIndex = null;
            clearTimeout(this.plpNavigationSyncFallbackTimeout);
            this.plpNavigationSyncFallbackTimeout = null;
            clearTimeout(this.scrollTimeout);
            this.isDragging = false;
            this.isMouseDown = false;
            this.hasSwipedRecently = false;
            this.skipNextClickPrevention = false;
            this.ignoreClickPreventionUntil = 0;
            this.resizeObserver?.disconnect();
            this.resizeObserver = null;

            if (this.slider) {
                this.slider.removeEventListener('scroll', this.boundUpdate);
                this.slider.removeEventListener('touchstart', this.boundOnDragStart);
                this.slider.removeEventListener('touchmove', this.boundOnDragMove);
                this.slider.removeEventListener('touchend', this.boundOnDragEnd);
                this.slider.removeEventListener('mousedown', this.boundOnMouseDown);
                this.slider.removeEventListener('mousemove', this.boundOnMouseMove);
                this.slider.removeEventListener('mouseup', this.boundOnMouseUp);
                this.slider.removeEventListener('mouseleave', this.boundOnMouseUp);
            }

            this.prevButton?.removeEventListener('click', this.boundOnButtonClick);
            this.nextButton?.removeEventListener('click', this.boundOnButtonClick);
            this.prevButtonMobile?.removeEventListener('click', this.boundOnButtonClick);
            this.nextButtonMobile?.removeEventListener('click', this.boundOnButtonClick);

            this.thumbnailItems?.forEach((thumbnail) => {
                thumbnail.removeEventListener("click", this.boundThumbnailClick);
            });

            this.removeClickPreventionListeners();
            this.pointerScrollCleanup?.();
            this.pointerScrollCleanup = null;
        }

        getPlpHitProductHandle() {
            const hitItem = this.closest?.(".ais-Hits-item") || this.closest?.("po-dynamic-product-card");
            const card = hitItem?.querySelector?.(".po-algolia-product-card") || hitItem;
            return card?.dataset?.plpProductHandle || card?.dataset?.laDnProductHandle || null;
        }

        getThumbnailForPlpHit() {
            const hitHandle = this.getPlpHitProductHandle();
            if (!hitHandle) {
                return this.thumbnailItems?.[0] || null;
            }

            return (
                Array.from(this.thumbnailItems || []).find(
                    (thumb) => thumb?.dataset?.currentVariant === hitHandle
                )
                || this.thumbnailItems?.[0]
                || null
            );
        }

        /**
         * Resolves the thumbnail for a slide using variant handle (avoids index drift on PLP).
         * @param {Element|null} slide
         * @returns {Element|null}
         */
        getThumbnailForSlide(slide) {
            const handle = slide?.dataset?.variantHandle;
            if (!handle) {
                return null;
            }

            const thumbnails = Array.from(this.thumbnailItems || []);
            return (
                thumbnails.find(
                    (thumb) => thumb?.dataset?.currentVariant === handle
                        && !thumb.hasAttribute("data-po-hidden")
                )
                || thumbnails.find((thumb) => thumb?.dataset?.currentVariant === handle)
                || null
            );
        }

        /**
         * Returns the thumbnail index for a visible slide.
         * @param {Element|null} slide
         * @returns {number}
         */
        getThumbnailIndexForSlide(slide) {
            const thumbnail = this.getThumbnailForSlide(slide);
            if (!thumbnail) {
                return -1;
            }

            return Array.from(this.thumbnailItems || []).indexOf(thumbnail);
        }

        getPlpHitSlideIndex() {
            const hitHandle = this.getPlpHitProductHandle();
            if (!hitHandle) {
                return 0;
            }

            const visibleSlides = this.sliderItemsToShow?.length
                ? Array.from(this.sliderItemsToShow)
                : Array.from(this.sliderItems || []).filter((slide) => slide.clientWidth > 0);
            const slideIndex = visibleSlides.findIndex(
                (slide) => slide?.dataset?.variantHandle === hitHandle
            );

            if (slideIndex >= 0) {
                return slideIndex;
            }

            const parsedIndex = Number.parseInt(this.dataset?.initialPlpSlideIndex || "0", 10);
            return Number.isFinite(parsedIndex) && parsedIndex >= 0 ? parsedIndex : 0;
        }

        syncPlpHitSelectionWithoutScroll() {
            if (!this.closest?.(".po-algolia-product-card")) {
                return;
            }

            const currentThumbnail = this.syncThumbnailSelectionToVisibleSlide();
            if (!currentThumbnail) {
                return;
            }

            if (this.productAnchor && currentThumbnail.dataset?.href) {
                this.productAnchor.href = currentThumbnail.dataset.href;
            }

            if (typeof this.changePlpInfo === "function" && !this.isPlpProductCardSlider()) {
                this.changePlpInfo(currentThumbnail);
            }
        }

        seedInitialPlpInfoCache() {
            const isAlgoliaPlpCard = Boolean(this.closest?.(".po-algolia-product-card"));
            const initialThumbnail = isAlgoliaPlpCard
                ? (this.thumbnailItems?.[0] || this.getThumbnailForPlpHit())
                : this.getThumbnailForPlpHit();
            const variantId = initialThumbnail?.getAttribute?.("data-current-variant");
            const cardRoot = PoSliderComponent.getPlpCardRoot(initialThumbnail);
            const algoliaTemplateSelectors = ["dinamic-price", "featured-badge", "wishlist-item", "top-badge"];

            if (!variantId || !cardRoot) {
                return;
            }

            const cardProductHandle = cardRoot.dataset?.plpProductHandle || cardRoot.dataset?.laDnProductHandle || "";
            if (isAlgoliaPlpCard && cardProductHandle && cardProductHandle !== variantId) {
                return;
            }

            algoliaTemplateSelectors.forEach((selector) => {
                const currentElement = cardRoot.querySelector(`.${selector}`);
                if (!currentElement) {
                    return;
                }

                PoSliderComponent.setResponseCache(`${variantId}-${selector}`, currentElement.innerHTML);
            });
        }

        /**
         * Refreshes PLP price, badges and OOS status for the selected color swatch.
         * @param {Element|null} thumbnail
         */
        bootstrapPlpInfo(thumbnail = null) {
            const thumb = thumbnail
                || this.querySelector('.po-product-card-thumbnails [data-current-variant][data-selected="true"]')
                || this.querySelector('.po-product-card-thumbnails [data-current-variant]')
                || this.thumbnailItems?.[0];

            if (!thumb) {
                return;
            }

            this.lastAppliedVariantId = null;
            return this.changePlpInfo(thumb);
        }

        async changePlpInfo(el) {
            if (el?.closest?.('po-plp-product-card')) {
                return;
            }

            const variantId = el?.getAttribute?.('data-current-variant');
            if (!variantId) return;
            if (variantId === this.lastAppliedVariantId) return;

            const plpInfoRequestId = ++this.plpInfoRequestId;

            const cardRoot = PoSliderComponent.getPlpCardRoot(el);
            if (!cardRoot) return;

            const thumbStatus = el?.dataset?.plpStatus?.trim() ?? '';
            if (thumbStatus) {
                PoSliderComponent.applyPlpProdStatus(cardRoot, thumbStatus);
            }

            const algoliaTemplateSelectors = ['dinamic-price', 'featured-badge', 'wishlist-item', 'top-badge'];
            if (!thumbStatus) {
                algoliaTemplateSelectors.push('prod-status');
            }
            
            const elementsToUpdate = [];
            const cachedUpdates = [];

            for (const selector of algoliaTemplateSelectors) {
                const currentElement = cardRoot.querySelector(`.${selector}`);
                if (!currentElement) continue;

                const cacheKey = `${variantId}-${selector}`;

                if (PoSliderComponent.responseCache.has(cacheKey)) {
                    cachedUpdates.push({ selector, element: currentElement, html: PoSliderComponent.responseCache.get(cacheKey) });
                } else {
                    elementsToUpdate.push({ selector, element: currentElement, cacheKey });
                }
            }

            for (const { selector, element, html } of cachedUpdates) {
                element.innerHTML = html;

                if (selector === 'dinamic-price') {
                    cardRoot.removeAttribute('data-plp-price-sync-pending');
                }
            }

            const isPricePending = elementsToUpdate.some(({ selector }) => selector === 'dinamic-price');
            if (!isPricePending) {
                cardRoot.removeAttribute('data-plp-price-sync-pending');
            }

            if (elementsToUpdate.length === 0) {
                this.lastAppliedVariantId = variantId;
                if (!thumbStatus) PoSliderComponent.syncPlpCardOosOpacity(cardRoot);
                return;
            }

            let fetchedStatusText = thumbStatus;
            let didUpdatePrice = false;

            try {
                const shopRoot = window.Shopify?.routes?.root || '/';
                const normalizedShopRoot = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;
                const metafieldsUrl = new URL(
                    `${normalizedShopRoot}products/${encodeURIComponent(variantId)}?view=metafields`,
                    window.location.origin
                );
                const response = await fetch(metafieldsUrl.toString());
                if (!response.ok) throw response;

                const parser = new DOMParser();
                const doc = parser.parseFromString(await response.text(), 'text/html');

                for (const { selector, element, cacheKey } of elementsToUpdate) {
                    const fetchedElement = doc.querySelector(`.${selector}`);
                    if (!fetchedElement) continue;

                    const newHTML = fetchedElement.innerHTML ?? '';
                    
                    PoSliderComponent.setResponseCache(cacheKey, newHTML);
                    element.innerHTML = newHTML;

                    if (selector === 'dinamic-price') {
                        didUpdatePrice = true;
                        cardRoot.removeAttribute('data-plp-price-sync-pending');
                    }

                    if (selector === 'prod-status') {
                        fetchedStatusText = doc.querySelector('[data-prod-status]')?.textContent?.trim() ?? '';
                    }
                }

                this.lastAppliedVariantId = variantId;

            } catch (error) {
                console.error(`Failed to update elements for variant ${variantId}:`, error.status || error);
            }

            if (plpInfoRequestId !== this.plpInfoRequestId) return;

            const activeVariantId = this.thumbnailItems?.[this.getClosestSlideIndex()]?.dataset?.currentVariant;
            if (activeVariantId !== variantId) return;

            if (!isPricePending || didUpdatePrice) {
                cardRoot.removeAttribute('data-plp-price-sync-pending');
            }

            if (!thumbStatus) {
                PoSliderComponent.applyPlpProdStatus(cardRoot, fetchedStatusText);
            }
        }

        update() {
            if (!this.slider || !this.nextButton || !this.nextButtonMobile) return;

            if (this.isLoopTransitioning && this.pendingLoopPage) {
                this.currentPage = this.pendingLoopPage;
                this.updateCurrentPageDisplay(this.pendingLoopPage);

                if (this.pageTotalElement) {
                    this.pageTotalElement.textContent = this.totalPages;
                }

                this.prevButton.removeAttribute('disabled');
                this.nextButton.removeAttribute('disabled');
                this.prevButtonMobile.removeAttribute('disabled');
                this.nextButtonMobile.removeAttribute('disabled');
                return;
            }

            const previousPage = this.currentPage;
            const currentSlideIndex = this.getClosestSlideIndex();
            const previousHandle = this.currentHandle;
            const currentSlide = this.sliderItemsToShow[currentSlideIndex] || null;
            this.currentPage = currentSlideIndex + 1;
            const currentThumbnail = this.getThumbnailForSlide(currentSlide)
                || this.thumbnailItems?.[currentSlideIndex]
                || null;
            const currentHandle = currentThumbnail?.dataset?.currentVariant
                || currentSlide?.dataset?.variantHandle
                || null;
            this.currentHandle = currentHandle;

            this.updateCurrentPageDisplay(this.currentPage);
            if (this.pageTotalElement) {
                this.pageTotalElement.textContent = this.totalPages;
            }

            if (this.currentPage != previousPage || currentHandle !== previousHandle) {
                let shouldSchedulePlpSync = true;

                if (Number.isFinite(this.plpNavigationTargetIndex)) {
                    if (currentSlideIndex !== this.plpNavigationTargetIndex) {
                        shouldSchedulePlpSync = false;
                    } else {
                        this.plpNavigationTargetIndex = null;
                    }
                }

                if (shouldSchedulePlpSync) {
                    this.schedulePlpSlideSync(currentSlideIndex);
                }
            }

            if (this.enableSliderLooping) {
                this.prevButton.removeAttribute('disabled');
                this.nextButton.removeAttribute('disabled');
                this.prevButtonMobile.removeAttribute('disabled');
                this.nextButtonMobile.removeAttribute('disabled');
                return;
            }

            if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
                this.prevButton.setAttribute('disabled', 'disabled');
                this.prevButtonMobile.setAttribute('disabled', 'disabled');
            } else {
                this.prevButton.removeAttribute('disabled');
                this.prevButtonMobile.removeAttribute('disabled');
            }

            if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
                this.nextButton.setAttribute('disabled', 'disabled');
                this.nextButtonMobile.setAttribute('disabled', 'disabled');
            } else {
                this.nextButton.removeAttribute('disabled');
                this.nextButtonMobile.removeAttribute('disabled');
            }
        }

        onDragStart(event) {
            this.isDragging = true;
            this.dragStartX = event.touches[0].clientX;
            this.dragStartY = event.touches[0].clientY;
            this.dragStartTime = Date.now();
            this.totalDragDistance = 0;
            this.totalDragDistanceY = 0;
            this.lastScrollLeft = this.slider.scrollLeft;
            this.hasSwipedRecently = false;
            this.skipNextClickPrevention = false;
        }

        onDragMove(event) {
            if (!this.isDragging) return;
            
            const currentX = event.touches[0].clientX;
            const currentY = event.touches[0].clientY;
            const deltaX = Math.abs(currentX - this.dragStartX);
            const deltaY = Math.abs(currentY - this.dragStartY);
            this.totalDragDistance = Math.max(this.totalDragDistance, deltaX);
            this.totalDragDistanceY = Math.max(this.totalDragDistanceY, deltaY);
            
            if (this.totalDragDistance > this.swipeThreshold) {
                this.hasSwipedRecently = true;
            }
        }

        onDragEnd(event) {
            if (!this.isDragging) return;
            
            const currentSlideIndex = this.getClosestSlideIndex();
            const maxScrollLeft = this.getMaxScrollLeft();
            const boundaryTolerance = 5;
            const isAtEnd = this.slider.scrollLeft >= maxScrollLeft - boundaryTolerance;
            const isAtStart = this.slider.scrollLeft <= boundaryTolerance;
            const dragDistance = this.dragStartX - event.changedTouches[0].clientX;
            const dragDuration = Date.now() - this.dragStartTime;
            const absDragDistance = Math.abs(dragDistance);
            
            const isSwipe =
                this.totalDragDistance > this.swipeThreshold ||
                (absDragDistance > this.swipeThreshold && dragDuration < 300);
            
            if (isSwipe) {
                this.hasSwipedRecently = true;
                setTimeout(() => {
                    this.hasSwipedRecently = false;
                }, this.clickPreventionDuration);
            }
            
            if (this.enableSliderLooping && isSwipe) {
                if (dragDistance > this.swipeThreshold && isAtEnd) {
                    this.runLoopTransition('next');
                    this.isDragging = false;
                    return;
                }

                if (dragDistance < -this.swipeThreshold && isAtStart) {
                    this.runLoopTransition('previous');
                    this.isDragging = false;
                    return;
                }
            }

            setTimeout(() => {
                this.isScrolling = false;
            }, 500);

            this.isDragging = false;

            const arrowClickAge = Date.now() - this.arrowClickAt;
            if (!isSwipe && this.arrowClickAt && arrowClickAge < 1500 && this.totalDragDistanceY < this.swipeThreshold) {
                const targetAnchor = event.target?.closest?.('a');
                const fallbackAnchor = targetAnchor || this.sliderItemsToShow[currentSlideIndex]?.querySelector('a');
                if (fallbackAnchor?.href) {
                    this.suppressNextClickUntil = Date.now() + this.clickPreventionDuration;
                    this.arrowClickAt = 0;
                    window.location.href = fallbackAnchor.href;
                }
            }
        }

        onMouseDown(event) {
            event.preventDefault();
            this.isMouseDown = true;
            this.dragStartX = event.clientX;
            this.dragStartTime = Date.now();
            this.totalDragDistance = 0;
            this.lastScrollLeft = this.slider.scrollLeft;
            this.hasSwipedRecently = false;
            this.skipNextClickPrevention = false;
            this.slider.style.cursor = 'grabbing';
        }

        onMouseMove(event) {
            if (!this.isMouseDown) return;
            event.preventDefault();
            
            const currentX = event.clientX;
            const deltaX = Math.abs(currentX - this.dragStartX);
            this.totalDragDistance = Math.max(this.totalDragDistance, deltaX);
            
            if (this.totalDragDistance > this.swipeThreshold) {
                this.hasSwipedRecently = true;
            }
            
            const dragDelta = event.clientX - this.dragStartX;
            this.slider.scrollLeft = this.lastScrollLeft - dragDelta;
        }

        onMouseUp(event) {
            if (!this.isMouseDown) return;
            
            this.isMouseDown = false;
            this.slider.style.cursor = 'grab';
            
            const dragDistance = event.clientX - this.dragStartX;
            const dragDuration = Date.now() - this.dragStartTime;
            
            const isSwipe = this.totalDragDistance > this.swipeThreshold || 
                        (Math.abs(dragDistance) > this.swipeThreshold && dragDuration < 300);
            
            if (isSwipe) {
                this.hasSwipedRecently = true;
                
                setTimeout(() => {
                    this.hasSwipedRecently = false;
                }, this.clickPreventionDuration);
                
                if (Math.abs(dragDistance) > 20) {
                    if (dragDistance < 0) {
                        this.nextButton.click();
                        this.nextButtonMobile.click();
                    } else {
                        this.prevButton.click();
                        this.prevButtonMobile.click();
                    }
                }
            }
        }

        isSlideVisible(element, offset = 0) {
            const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
            return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
        }

        onButtonClick(event) {
            event.preventDefault();
            if (this.isLoopTransitioning) return;

            this.hasSwipedRecently = false;
            this.isDragging = false;
            this.isMouseDown = false;
            this.totalDragDistance = 0;
            this.skipNextClickPrevention = true;
            this.ignoreClickPreventionUntil = Date.now() + this.clickPreventionDuration;
            this.arrowClickAt = Date.now();
            const step = Number(event.currentTarget.dataset.step) || 1;
            
            let newSlideIndex;
            const currentSlideIndex = this.getClosestSlideIndex();
            const isNextButton = event.currentTarget.name === 'next' || event.currentTarget.name === 'next_mobile';
            const isAtLastSlide = currentSlideIndex >= this.sliderItemsToShow.length - 1;
            const isAtFirstSlide = currentSlideIndex <= 0;
            
            if (isNextButton) {
                if (this.enableSliderLooping && isAtLastSlide) {
                    this.runLoopTransition('next');
                    return;
                } else {
                    newSlideIndex = Math.min(currentSlideIndex + step, this.sliderItemsToShow.length - 1);
                }
            } else {
                if (this.enableSliderLooping && isAtFirstSlide) {
                    this.runLoopTransition('previous');
                    return;
                } else {
                    newSlideIndex = Math.max(currentSlideIndex - step, 0);
                }
            }
            
            this.slideScrollPosition = this.getSlidePositionByIndex(newSlideIndex);
            this.navigateToSlideIndex(newSlideIndex);
        }

        /**
         * Navigates to a slide using the same path as arrow controls (scroll → slideChanged).
         * @param {number} slideIndex
         * @param {{ behavior?: ScrollBehavior|string, forceSync?: boolean }} [options]
         */
        navigateToSlideIndex(slideIndex, options = {}) {
            const slide = this.sliderItemsToShow?.[slideIndex];
            if (!slide) {
                return;
            }

            clearTimeout(this.plpSlideSyncTimeout);

            const behavior = options.behavior ?? this.getPreferredScrollBehavior();
            const targetPosition = this.getSlidePositionByIndex(slideIndex);
            const currentPosition = this.slider?.scrollLeft ?? 0;
            const positionChanged = Math.abs(targetPosition - currentPosition) > 1;

            this.plpNavigationTargetIndex = positionChanged ? slideIndex : null;
            this.slideScrollPosition = targetPosition;
            this.updateSelectedThumbnail(slideIndex);
            this.setSlidePosition(targetPosition, behavior);

            if (!positionChanged || options.forceSync) {
                this.plpNavigationTargetIndex = null;
                this.lastPlpSyncedHandle = null;
                this.applyPlpSlideSync(slideIndex);
                return;
            }

            const targetIndex = slideIndex;
            clearTimeout(this.plpNavigationSyncFallbackTimeout);
            this.plpNavigationSyncFallbackTimeout = setTimeout(() => {
                this.plpNavigationSyncFallbackTimeout = null;

                if (this.plpNavigationTargetIndex !== targetIndex) {
                    return;
                }

                this.plpNavigationTargetIndex = null;
                this.lastPlpSyncedHandle = null;
                this.applyPlpSlideSync(targetIndex);
            }, this.getLoopTransitionDuration() + 200);
        }

        /**
         * Resolves a visible slide index from a thumbnail element.
         * @param {Element|null} thumbnail
         * @returns {number}
         */
        getSlideIndexForThumbnail(thumbnail) {
            const thumbnailIndex = thumbnail
                ? Array.from(this.thumbnailItems || []).indexOf(thumbnail)
                : -1;

            if (thumbnailIndex < 0) {
                return -1;
            }

            const handle = thumbnail?.dataset?.currentVariant;
            if (!handle) {
                return thumbnailIndex;
            }

            const slideIndex = Array.from(this.sliderItemsToShow || []).findIndex(
                (slide) => slide?.dataset?.variantHandle === handle
            );

            return slideIndex >= 0 ? slideIndex : thumbnailIndex;
        }

        updateCurrentPageDisplay(pageNumber) {
            if (this.currentPageElement) {
                this.currentPageElement.textContent = pageNumber;
            }
            if (this.currentPageElementMobile) {
                this.currentPageElementMobile.textContent = pageNumber;
            }
        }

        getPreferredScrollBehavior() {
            return window.matchMedia('(max-width: 1023px)').matches ? 'auto' : 'smooth';
        }

        getLoopTransitionDuration() {
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return 0;
            }

            return 450;
        }

        disableSliderNativeScrollEffects() {
            if (!this.slider) return;

            this.savedInlineScrollBehavior = this.slider.style.scrollBehavior;
            this.savedInlineScrollSnapType = this.slider.style.scrollSnapType;
            this.savedInlineOverflowAnchor = this.slider.style.overflowAnchor;

            this.slider.style.scrollBehavior = 'auto';
            this.slider.style.scrollSnapType = 'none';
            this.slider.style.overflowAnchor = 'none';
        }

        restoreSliderNativeScrollEffects() {
            if (!this.slider) return;

            this.slider.style.scrollBehavior = this.savedInlineScrollBehavior;
            this.slider.style.scrollSnapType = this.savedInlineScrollSnapType;
            this.slider.style.overflowAnchor = this.savedInlineOverflowAnchor;
        }

        runLoopTransition(direction) {
            if (
                !this.enableSliderLooping ||
                this.isLoopTransitioning ||
                !this.sliderItemsToShow ||
                this.sliderItemsToShow.length < 2
            ) {
                return false;
            }

            const lastSlideIndex = this.sliderItemsToShow.length - 1;
            const targetIndex = direction === 'next' ? 0 : lastSlideIndex;
            const targetPosition = this.getSlidePositionByIndex(targetIndex);

            this.applyPlpSlideSync(targetIndex);
            this.pendingLoopPage = targetIndex + 1;
            this.currentPage = this.pendingLoopPage;
            this.updateCurrentPageDisplay(this.pendingLoopPage);

            const sourceSlide = direction === 'next'
                ? this.sliderItemsToShow[0]
                : this.sliderItemsToShow[lastSlideIndex];

            if (!sourceSlide) {
                this.pendingLoopPage = null;
                return false;
            }

            const cloneSlide = sourceSlide.cloneNode(true);
            cloneSlide.removeAttribute('id');
            cloneSlide.dataset.loopClone = 'true';

            this.isLoopTransitioning = true;
            this.isScrolling = true;
            clearTimeout(this.loopTransitionTimeout);
            this.disableSliderNativeScrollEffects();

            if (direction === 'next') {
                this.slider.appendChild(cloneSlide);
                requestAnimationFrame(() => {
                    this.setSlidePosition(cloneSlide.offsetLeft, 'smooth');
                });
            } else {
                this.slider.prepend(cloneSlide);
                const loopOffset = cloneSlide.clientWidth || this.sliderItemOffset || this.slider.clientWidth || 0;

                this.setSlidePosition(this.slider.scrollLeft + loopOffset, 'auto');
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        this.setSlidePosition(0, 'smooth');
                    });
                });
            }

            this.loopTransitionTimeout = setTimeout(() => {
                const resetPosition = direction === 'next'
                    ? targetPosition
                    : targetPosition + (cloneSlide.clientWidth || this.sliderItemOffset || this.slider.clientWidth || 0);

                this.slider.scrollLeft = resetPosition;
                cloneSlide.remove();
                this.isLoopTransitioning = false;
                this.isScrolling = false;
                this.pendingLoopPage = null;

                requestAnimationFrame(() => {
                    this.restoreSliderNativeScrollEffects();
                    this.update();
                });
            }, this.getLoopTransitionDuration());

            return true;
        }

        schedulePlpSlideSync(slideIndex) {
            clearTimeout(this.plpSlideSyncTimeout);
            this.plpSlideSyncTimeout = setTimeout(() => {
                this.plpSlideSyncTimeout = null;
                this.applyPlpSlideSync(slideIndex);
            }, 100);
        }

        applyPlpSlideSync(slideIndex) {
            const slide = this.sliderItemsToShow?.[slideIndex];
            if (!slide) return;

            const currentThumbnail = this.getThumbnailForSlide(slide)
                || this.thumbnailItems?.[slideIndex]
                || null;
            const currentHandle = currentThumbnail?.dataset?.currentVariant
                || slide?.dataset?.variantHandle
                || null;

            if (currentHandle && currentHandle === this.lastPlpSyncedHandle) {
                return;
            }

            this.lastPlpSyncedHandle = currentHandle;
            clearTimeout(this.plpNavigationSyncFallbackTimeout);
            this.plpNavigationSyncFallbackTimeout = null;
            this.updateSelectedThumbnail(slideIndex);
            this.dispatchEvent(
                new CustomEvent('slideChanged', {
                    detail: {
                        currentPage: slideIndex + 1,
                        currentElement: slide,
                        currentThumbnail,
                        currentHandle,
                    },
                }),
            );
        }

        updateSelectedThumbnail(slideIndex) {
            if (this.thumbnailWrapper && this.thumbnailItems.length > 0) {
                const previousSelectedThumbnail = this.thumbnailWrapper.querySelector("[data-selected=true]");
                if (previousSelectedThumbnail) previousSelectedThumbnail.dataset.selected = "false";

                const slide = this.sliderItemsToShow?.[slideIndex] || null;
                const currentThumbnail = this.getThumbnailForSlide(slide)
                    || this.thumbnailItems[slideIndex];
                if (currentThumbnail) {
                    currentThumbnail.dataset.selected = "true";
                    if (this.productAnchor) {
                        this.productAnchor.href = currentThumbnail.dataset.href;
                    }

                    if (!this.closest('po-plp-product-card')) {
                        this.changePlpInfo(currentThumbnail);
                    }

                    
                    if (currentThumbnail.closest('.po-product-card-thumbnails')) {
                        const container = currentThumbnail.closest('.po-product-card-thumbnails');
                        const scrollPos = currentThumbnail.offsetLeft - (container.offsetWidth / 2) + (currentThumbnail.offsetWidth / 2);
                        container.scrollTo({ left: Math.max(0, scrollPos), behavior: 'smooth' });
                    }
                }
            }
        }

        setSlidePosition(position, behavior) {
            const boundedPosition = Math.min(Math.max(position, 0), this.getMaxScrollLeft());
            const options = { left: boundedPosition };
            if (behavior) {
                options.behavior = behavior;
            }
            this.slider.scrollTo(options);
        }

        onThumbnailClick(event, thumbnail) {
            event.preventDefault();

            const resolvedSlideIndex = this.getSlideIndexForThumbnail(thumbnail);
            if (resolvedSlideIndex < 0) {
                return;
            }

            this.hasSwipedRecently = false;
            this.isDragging = false;
            this.isMouseDown = false;
            this.totalDragDistance = 0;
            this.skipNextClickPrevention = true;
            this.ignoreClickPreventionUntil = Date.now() + this.clickPreventionDuration;
            this.navigateToSlideIndex(resolvedSlideIndex);
        }

        pointerScroll(elem) {
            let thumbnail;
            const dragStart = (ev) => {
                ev.preventDefault();
                elem.setPointerCapture(ev.pointerId);
                thumbnail = ev.target;
            }
            
            const dragEnd = (ev) => {
                ev.preventDefault();
                elem.releasePointerCapture(ev.pointerId);
                if (!this.thumbnailDragging && thumbnail) {
                    thumbnail.click();
                }
                this.thumbnailDragging = false;
            }
            
            const drag = (ev) => {
                ev.preventDefault();
                if (elem.hasPointerCapture(ev.pointerId)) {
                    elem.scrollLeft -= ev.movementX;
                    this.thumbnailDragging = true;
                }
            }
            
            elem.addEventListener("pointerdown", dragStart);
            elem.addEventListener("pointerup", dragEnd);
            elem.addEventListener("pointermove", drag);

            return () => {
                elem.removeEventListener("pointerdown", dragStart);
                elem.removeEventListener("pointerup", dragEnd);
                elem.removeEventListener("pointermove", drag);
            };
        }
    }
    class PoCharacterLimiter extends HTMLElement {
        connectedCallback() {
            this.limit = parseInt(this.dataset.limit) || 0;
            this.readMoreBtn = this.querySelector(".js-read-more");
            this.visibleSpan = this.querySelector(".js-text");
            this.hiddenSpan = this.querySelector(".js-hidden-text");
            this.readMoreStr = this.dataset.readMore || "Read more";
            this.readLess = this.dataset.readLess || "Read less";

            if ((!this.visibleSpan || !this.readMoreBtn || !this.hiddenSpan || this.limit <= 0) || (this.visibleSpan.textContent.length <= this.limit)) {
                this.readMoreBtn?.remove();
                return;
            }

            this.setupTexts();
            this.readMoreBtn.addEventListener("click", () => {
                this.hiddenSpan.classList.toggle("tw-hidden")
                this.readMoreBtn.textContent = this.hiddenSpan.classList.contains("tw-hidden") ? this.readMoreStr : this.readLess;
            });
        }

        setupTexts = () => {
            const stringArray = this.visibleSpan.textContent.split(" ");
            let characterAccumulator = 0;
            let indexToSlice = 0;
            for (let index = 0; index < stringArray.length; index++) {
                const word = stringArray[index];
                characterAccumulator += word.length + 1; // +1 for space
                if (characterAccumulator > this.limit) {
                    indexToSlice = index;
                    break;
                }
            }

            this.visibleSpan.textContent = stringArray.slice(0, indexToSlice).join(" ") + "...";
            this.hiddenSpan.textContent = " " + stringArray.slice(indexToSlice).join(" ");
        }
    }

    class PoSwiper extends HTMLElement {
        connectedCallback () {
            this.config = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent ?? "{}");
            this.zoomInButton = this.querySelector("[data-zoom-in]");
            this.zoomOutButton = this.querySelector("[data-zoom-out]");
            if (this.config.init !== true) this.config.init = false;
            this.singleClickZoom = this.dataset.singleClickZoom === "true";
            this.waitForInit = this.dataset.waitForInit === "true";
            this.removeClass = this.dataset.removeClass;
            this.loaderEl = this.querySelector("[data-loader]");
            this.loaded = false;
            this.originalSlidesOrder = [];

            this.config.on = this.config.on || {};

            this.config.on.init = this.onSwiperInit.bind(this);

            // Setup single click zoom
            if (this.config.zoom || this.singleClickZoom) {
                this.config.on.click = function (swiper, event) {
                    const zoom = swiper.zoom;
                    if (zoom.scale && zoom.scale !== 1) {
                        zoom.out();
                    } else {
                        zoom.in(event.target);
                    }
                };
            }

            this.swiper = window.Swiper ? new window.Swiper(this, this.config) : null;
            this.control = this.dataset.control ? document.querySelector(this.dataset.control) : null;

            this.dialog = this.closest("po-dialog") || this.closest("dialog");

            this.mutationObserver = new MutationObserver((mutations) => {
                mutations.forEach(({ type, attributeName, target }) => {
                    if (type === "attributes" && attributeName?.includes("open") && target === this.dialog) this.startSwiper();
                });
            });

            if (this.dialog) {
                this.mutationObserver.observe(this.dialog, {
                    attributes: true,
                    attributeFilter: ["open", "data-open"],
                });
            } else if (!this.waitForInit) {
                this.startSwiper();
            }
        }

        startSwiper = () => {
            if (this.loaded) return;

            if (this.swiper) {
                this.swiper.on("afterInit", () => {
                    this.dispatchEvent(new CustomEvent('swiper:afterInit'))
                });

                this.swiper.on("slideChange", () => {
                    this.dispatchEvent(new CustomEvent("slideChanged", {
                        detail: {
                            activeIndex: this.swiper.activeIndex,
                            previousIndex: this.swiper.previousIndex,
                            realIndex: this.swiper.realIndex,
                        }
                    }));

                    if (this.zoomOutButton) this.zoomOutButton.disabled = true;
                    if (this.zoomInButton) this.zoomInButton.disabled = false;
                });

                // Listen to zoom changes (when zooming with mouse/touch)
                this.swiper.on("zoomChange", (swiper, scale, imageEl, slideEl) => {
                    const isZoomed = scale > 1;
                    if (this.zoomInButton) this.zoomInButton.disabled = isZoomed;
                    if (this.zoomOutButton) this.zoomOutButton.disabled = !isZoomed;
                });

                this.zoomInButton?.addEventListener('click', () => {
                    this.swiper.zoom.in();
                    if (this.zoomInButton) this.zoomInButton.disabled = true;
                    if (this.zoomOutButton) this.zoomOutButton.disabled = false;
                });

                this.zoomOutButton?.addEventListener('click', () => {
                    this.swiper.zoom.out();
                    if (this.zoomOutButton) this.zoomOutButton.disabled = true;
                    if (this.zoomInButton) this.zoomInButton.disabled = false;
                });

                if (this.config && !this.config.init) {
                    this.swiper.init();
                    if (this.control && this.control.swiper) {
                        this.swiper.controller.control = this.control.swiper;
                    } else if (this.control) {
                        this.control.addEventListener('swiper:afterInit', () => {
                            this.swiper.controller.control = this.control.swiper
                            this.swiper.pagination.update();
                        });
                    }
                }

                // Store references to original slides
                this.originalSlidesOrder = Array.from(this.querySelectorAll('.swiper-slide'));

                this.classList.add('visible-swiper-slides');
                this.toggleAttribute("data-loaded", true);
                this.loaded = true;               

                /** Due to figma changes, the feature to move the monogram slide to the start 
                 * is NO LONGER NEEDED. 
                 * (Mantained here for possible future use cases) 
                */
               /*
                subscribe(PUB_SUB_EVENTS.monogramApplied, function(eventData) {
                    // Check if the element has the class (check for pdp)
                    const parentGallery = this.closest('.po-product-media-gallery');
                    if (parentGallery) {
                    this.moveSlideToStart(3);
                    }
                }.bind(this)); 

                // Subscribe to the monogramRemoved event
                subscribe(PUB_SUB_EVENTS.monogramRemoved, function(eventData) {
                    // Check if the element has the class (check for pdp)
                    const parentGallery = this.closest('.po-product-media-gallery');
                    if (parentGallery) {
                    this.restoreSlides();
                    }
                }.bind(this));
                */
                
            }
            else {
                console.error('Failed to load Swiper');
            }
        }
        
        /**
         * Moves a slide (by index) to the start of the swiper and refreshes layout
         */
        moveSlideToStart(slideIndex) {
            const slides = this.swiper.slides;
            const slideToMove = slides[slideIndex];

            if (!slideToMove) {
                console.warn(`Slide at index ${slideIndex} not found`);
                return;
            }

            // Move slide to the first position
            this.swiper.wrapperEl.prepend(slideToMove);

            
            this.updateSwiper(0);
        }
        
        // Handle the monogram removed event
        restoreSlides(slideIndex) {
            if (!this.originalSlidesOrder) return;

            if (typeof slideIndex === 'number') {
                // Restore only the specified slide
                const slide = this.originalSlidesOrder[slideIndex];
                if (!slide) return;

                const referenceSlide = this.originalSlidesOrder[slideIndex + 1] || null;
                this.swiper.wrapperEl.insertBefore(slide, referenceSlide);
            } else {
                // Restore all slides to their original order
                this.originalSlidesOrder.forEach(slide => {
                    this.swiper.wrapperEl.appendChild(slide);
                });
            }

            this.updateSwiper(0);
        }

        /**
         * Perform all necessary Swiper updates after changing slide order or content.
         * @param {number} [slideTo] - Optional: slide index to navigate to after update.
         */
        updateSwiper(slideTo) {
            this.swiper.update();
            if (typeof slideTo === 'number') {
                this.swiper.slideTo(slideTo);
            }
            this.swiper.pagination.update();
            this.swiper.updateSlides();
            this.swiper.updateAutoHeight();
            this.updateAriaLabels();
        }

        // Update the aria-labels for accessibility
        updateAriaLabels() {
            const slides = this.swiper.slides;
            slides.forEach((slide, index) => {
                slide.setAttribute('aria-label', `${index + 1} / ${slides.length}`);
            });
        }

        onSwiperInit () {
            this.toggleAttribute("data-loaded", true);

            if (this.loaderEl) {
                this.loaderEl.style.display = "none";
            }

            if (this.removeClass) {
                this.querySelector(".swiper-wrapper")?.classList.remove(this.removeClass);
            }
        }

        disconnectedCallback() {
            this.swiper?.destroy();
        }
    };

     class PoDialog extends HTMLElement {
        connectedCallback() {
            this.animationClass = this.dataset.animationClass || "";
            this.closeButtons = this.querySelectorAll(".js-po-modal-close");

            this.closeButtons.forEach(btn => btn.addEventListener("click", this.close));
            window.addEventListener("keydown", this.window_keydownHandler);
        }

        disconnectedCallback() {
            this.closeButtons.forEach(btn => btn.removeEventListener("click", this.close));
            window.removeEventListener("keydown", this.window_keydownHandler);
        }

        window_keydownHandler = (event) => {
            if (event.key?.toLowerCase() === "escape") {
                this.close();
            }
        }

        showModal = () => {
            this.dataset.open = "true";
            this.classList.remove("tw-invisible", "tw-opacity-0");
            if (this.animationClass) this.classList.add(this.animationClass);

            this.dispatchEvent(new CustomEvent("open"));
        }

        open = () => {
            this.dataset.open = "true";
            this.classList.remove("tw-invisible", "tw-opacity-0");
            this.classList.add("tw-animate-slide-left");

            this.dispatchEvent(new CustomEvent("open"));
        }
        close = () => {
            this.dataset.open = "false";
            this.classList.add("tw-invisible", "tw-opacity-0");
            if (this.animationClass) this.classList.remove(this.animationClass);
            this.dispatchEvent(new CustomEvent("close"));
        }

        disconnectedCallback() {
            this.querySelectorAll(".js-po-modal-close").forEach(btn => btn.removeEventListener("click", this.close));
        }
    }

    class OpIntersectionSticky extends HTMLElement {
        connectedCallback () {
            this.placement = this.dataset.placement;
            this.hideTarget = document.getElementById(this.dataset.hideTarget);
            this.threshold = parseFloat(this.dataset.threshold || "0");

            this.observer = new IntersectionObserver(this.intersectionCallback.bind(this), {
                root: null,
                rootMargin: "0px",
                threshold: this.threshold
            });

            if (this.hideTarget) {
                this.observer.observe(this.hideTarget);
            }
        }

        disconnectedCallback () {
            this.observer?.disconnect();
        }

        canShow (targetRect) {
            const elementRect = this.getBoundingClientRect();

            switch (this.placement) {
                case "bottom":
                    return targetRect.top > elementRect.top;
                case "top":
                    return targetRect.bottom < elementRect.bottom;
                default:
                    return true;
            }

        }

        intersectionCallback (entries) {
            entries.forEach((entry) => {
                const targetRect = entry.target.getBoundingClientRect();
                !entry.isIntersecting && this.canShow(targetRect) ? this.show() : this.hide();
            });
        }

        hide () {
            this.style.display = "none";
        }

        show () {
            this.style.removeProperty("display");
        }
    };

    class PoDeliveryEstimator extends HTMLElement {
        connectedCallback () {
            const configEstimationDays = [];

            this.zip = this.dataset.zip;
            this.country = this.dataset.country;
            this.province = this.dataset.province;
            this.label = this.dataset.label;
            this.appendText = this.dataset.appendText;
            this.preorderDate = this.dataset.preorderDate;

            this.fetchUrl = `${window.routes?.cart_url}/shipping_rates.json`;
            const rawLocale = window.Shopify?.locale || "en";
            const localeParts = rawLocale.split("-");
            const language = (localeParts[0] || "en").toLowerCase();
            const regionFromLocale = (localeParts[1] || "").toUpperCase();
            const region = (window.Shopify?.country || regionFromLocale || "US").toUpperCase();
            this.localeCountry = `${language}-${region}`;

            this.dataset.estimationDays?.split("-")?.forEach(day => {
                const dayN = parseInt(day.trim());

                if (!isNaN(dayN) && dayN >= 0) {
                    configEstimationDays.push(dayN);
                }
            });

            if (configEstimationDays.length > 0) {
                this.updateDeliveryEstimate(configEstimationDays);
            } else if (this.zip && this.country && this.province) {
                this.estimateDelivery();
            }

        }

        estimateDelivery () {
            const params = new URLSearchParams();

            params.append("shipping_address[zip]", this.zip);
            params.append("shipping_address[country]", this.country);
            params.append("shipping_address[province]", this.province);

            fetch(`${this.fetchUrl}?${params.toString()}`, { method: "GET" })
                .then(response => {
                    if (!response.ok) {
                        throw new Error("Error:", response.status);
                    }

                    return response.json();
                })
                .then(data => {
                    const shippingRates = data.shipping_rates || [];
                    const deliveryDays = shippingRates[0]?.delivery_days;

                    this.updateDeliveryEstimate(deliveryDays);
                })
                .catch(error => {
                    console.error("Error fetching delivery estimate", error);
                });
        }

        updateDeliveryEstimate (days = []) {
            let textOut = days.map(day => {
                let startDate;
            
                if (this.preorderDate) {
                    const dateParts = this.preorderDate.split('/');
                    if (dateParts.length === 3) {
                        const day = parseInt(dateParts[0], 10);
                        const month = parseInt(dateParts[1], 10) - 1;
                        const year = parseInt(dateParts[2], 10);
                        
                        startDate = new Date(year, month, day);
                        
                        if (isNaN(startDate.getTime())) {
                            startDate = new Date();
                        }
                    } else {
                        startDate = new Date();
                    }
                } else {
                    startDate = new Date();
                }
                
                const deliveryDate = new Date(startDate);

                let remainingBusinessDays = day;
                while (remainingBusinessDays > 0) {
                    deliveryDate.setDate(deliveryDate.getDate() + 1);
                    const weekDay = deliveryDate.getDay();
                    if (weekDay !== 0 && weekDay !== 6) {
                        remainingBusinessDays -= 1;
                    }
                }

                // If the computed day lands on weekend (e.g. day = 0), shift to next business day.
                while (deliveryDate.getDay() === 0 || deliveryDate.getDay() === 6) {
                    deliveryDate.setDate(deliveryDate.getDate() + 1);
                }
                
                try {
                return new Intl.DateTimeFormat(this.localeCountry, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                }).format(deliveryDate);
                } catch (e) {
                    return new Intl.DateTimeFormat("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                    }).format(deliveryDate);
                }
            }).join(" - ");

            if (this.appendText) {
                textOut += ` ${this.appendText}`;
            }

            this.textContent = textOut;
        }
    };

    class OpScrollProgress extends HTMLElement {
        connectedCallback () {
            this.progressEl = this.querySelector("[data-scroll-progress]");
            this.container = document.querySelector(this.dataset.scrollContainer);
            
            if (!this.progressEl) return;
            
            this.updateScrollBar();
            window.addEventListener('scroll', this.updateScrollBar);
        };
        
        updateScrollBar = () => {
            this.containerHeight = this.container ? this.container.offsetHeight : window.innerHeight;
            const scrollTop = window.scrollY;
            const scrollHeight = document.documentElement.scrollHeight - this.containerHeight;
            const scrollPercent = (scrollTop / scrollHeight) * 100;
      
            this.progressEl.style.height = scrollPercent > 100 ? "100%" : scrollPercent + '%';
        }
    };

    class PoDarkToggle extends HTMLElement {
        connectedCallback () {
            this.darkMode = this.dataset.darkMode;
            this.darkModeToggle = document.querySelector(`[data-dark-mode-toggle="${this.darkMode}"]`);
            this.darkModeToggle?.addEventListener("click", this.toggleDarkMode.bind(this));
        }

        toggleDarkMode () {
            if (this.dataset.darkMode) {
                document.documentElement.classList.toggle(this.dataset.darkMode);
            }
        }
    }

    class PoProgressIndicator extends HTMLElement {
        static observedAttributes = ["value", "max"];
        
        connectedCallback() {
            this.progressBar = this.querySelector('.js-progress-bar');
            if (!this.progressBar) {
                console.error("Progress bar element not found in OpProgressIndicator.", this);
                return;
            }
            this.updateProgress();
        }

        attributeChangedCallback() {
            this.updateProgress();
        }

        updateProgress() {
            const progress = parseFloat(this.getAttribute("value")) || 0;
            const max = parseFloat(this.getAttribute("max")) || 100;
            const percentualProgress = (progress * 100) / max;
            if (this.progressBar) this.progressBar.style.width = `${percentualProgress}%`;
        }

        startLoading() {
            this.loadingInterval = setInterval(() => {
                this.setAttribute("value", Math.min(parseFloat(this.getAttribute("value")) + 10, parseFloat(this.getAttribute("max")) || 100));
                if (parseFloat(this.getAttribute("value")) >= parseFloat(this.getAttribute("max"))) clearInterval(this.loadingInterval);
            }, 50);
        }
    }

    class PoRTRViewerList extends HTMLElement {
        constructor() {
            super();

            // Container
            this.sectionId = this.dataset.sectionId;
            this.container = this.querySelector(`#rtr-container-${this.sectionId}`);

            if (this.container) {
                this.desktopBackground = this.container.dataset.desktopBackground;
                this.mobileBackground = this.container.dataset.mobileBackground;
                this.defaultBackground = 'https://rtr-viewer.luxottica.com/asset/background/gradient-square-srgb.jpg';

                // Nuovi parametri opzionali per controllare il posizionamento orizzontale
                // Default: sposta il viewer più a sinistra su desktop (0.35 anziché 0.5)
                this.desktopBoundLeft = parseFloat(this.container.dataset.desktopBoundLeft ?? '0.35');
                this.desktopBoundRight = parseFloat(this.container.dataset.desktopBoundRight ?? '1');
                this.mobileBoundLeft = parseFloat(this.container.dataset.mobileBoundLeft ?? '0');
                this.mobileBoundRight = parseFloat(this.container.dataset.mobileBoundRight ?? '1');
            }

            // Changers
            this.rtrChangers = this.querySelectorAll('.rtr-changer');
            if (this.rtrChangers.length > 1) {
                this.defaultProduct = Array.from(this.rtrChangers).find(input => input.checked);
                if (this.defaultProduct) {
                    this.defaultProductUpc = this.defaultProduct.value;
                }
                this.rtrChangers.forEach((input) => input.addEventListener('change', this.handleChange.bind(this)));
            } else if (this.rtrChangers.length === 1) {
                this.defaultProductUpc = this.rtrChangers[0].value;
            }

            this.swiperElement = this.querySelector(`.swiper-${this.sectionId}`);

            // Blocks
            this.rtrBlockTitle = this.querySelector('.rtr-block-title-wrapper');
            this.rtrBlockText = this.querySelector('.rtr-block-text-wrapper');
            this.rtrBlocCta = this.querySelector('.rtr-cta--block');

            // CTA Update
            this.ctaToUpdate = this.querySelector('.rtr-product--cta');

            // Resize event listener
            this.handleResize = this.handleResize.bind(this);
            window.addEventListener('resize', this.handleResize);

            // Init object with settings
            const isDesktop = window.innerWidth >= 1024;
            const bounds = isDesktop
                ? { left: this.desktopBoundLeft, right: this.desktopBoundRight }
                : { left: this.mobileBoundLeft, right: this.mobileBoundRight };

            this.initInput = {
                data: {
                    selector: `#${this.container?.id}`,
                    id: {
                        type: 'upc', // moco or token
                        value: `${this.defaultProductUpc}`, // or any other valid UPC, MoCo or token
                    },
                    backgroundPath: isDesktop
                        ? (this.desktopBackground || 'https://rtr-viewer.luxottica.com/asset/background/gradient-square-srgb.jpg')
                        : (this.mobileBackground || 'https://rtr-viewer.luxottica.com/asset/background/gradient-square-srgb.jpg'),
                    settings: {
                        autoResetCamera: true,
                        autoRotateCamera: false,
                        autoRotateSpeed: 20,
                        backgroundFit: 'fill',
                        boundingArea: {
                            topLeft: { x: bounds.left, y: 0 },
                            bottomRight: { x: bounds.right, y: 1 },
                        },
                        boundingFit: 'auto',
                        cameraAnimationTime: 0.3,
                        cameraRotationInitial: { phi: 0, theta: 0 },
                        cameraRotationReset: { phi: 0, theta: 0 },
                        clearColor: '#ffffff',
                        gestures: {
                            mouse: { left: 'rotate', middle: 'none', right: 'pan', wheel: 'none' },
                            touches: { one: 'rotate', two: 'dolly-pan', three: 'none' },
                        },
                        lensesName: undefined,
                        lensesOpacity: 1,
                        orbitPoint: true,
                        qualityMoving: 4,
                        qualityStill: 5,
                        scrollInteraction: false,
                        showBackground: true,
                        showEnvironment: false,
                        transmission: true,
                    }
                },
                callbacks: {
                    onRendered: () => this.handleOnRendered()
                },
            };
        }

        connectedCallback() {
            const boot = () => {
                this.rtrViewer = window.rtrViewer; // salviamo su this
                this.rtrViewer.init(this.initInput);

                this.swiper = new Swiper(this.swiperElement, {
                    slidesPerView: 'auto',
                    slidesPerGroupAuto: true,
                    spaceBetween: 16,
                    updateOnWindowResize: true,
                    loop: false,
                    grabCursor: true
                });
            };

            if (document.readyState === 'loading') {
                window.addEventListener('DOMContentLoaded', boot, { once: true });
            } else {
                boot();
            }
        }

        handleOnRendered() {
            if (this.rtrChangers.length > 1) {
                this.rtrChangers.forEach((input) => {
                    if (input.disabled) input.removeAttribute('disabled');
                });
            }
        }

        handleChange(event) {
            const selectedValue = event.target.value;

            // Disable Changers to avoid more than one switch at a time before rendering
            if (this.rtrChangers.length > 1) {
                this.rtrChangers.forEach((input) => {
                    if (!input.disabled) {
                        // input.setAttribute('disabled', '');
                        input.removeAttribute('checked');
                    }
                });
            }

            event.target.setAttribute('checked', '');

            this.handleInfoChange(event.currentTarget);

            // Update CTA
            if (this.ctaToUpdate) {
                this.ctaToUpdate.innerHTML = event.target.dataset.productTitle;
                this.ctaToUpdate.closest('a')?.setAttribute('title', event.target.dataset.productTitle);
                this.ctaToUpdate.closest('a')?.setAttribute('href', event.target.dataset.variantUrl);
            }

            // Update upc ID
            if (selectedValue && this.rtrViewer) {
                this.rtrViewer.setId({
                    type: 'upc',
                    value: `${event.target.value}`,
                });
            }
        }

        handleInfoChange(element) {
            if (element.dataset.blockTitle != '') {
                this.rtrBlockTitle.innerHTML = element.dataset.blockTitle;
                this.rtrBlockTitle.classList.remove('tw-hidden');
            } else {
                this.rtrBlockTitle.classList.add('tw-hidden');
            }

            if (element.dataset.blockText != '') {
                this.rtrBlockText.innerHTML = element.dataset.blockText;
                this.rtrBlockText.classList.remove('tw-hidden');
            } else {
                this.rtrBlockText.classList.add('tw-hidden');
            }

            if (this.rtrBlocCta) {
                this.updateCta(
                    this.rtrBlocCta,
                    element.dataset.blockCtaLabel,
                    element.dataset.blockCtaUrl
                );
            }
        }

        updateCta(ctaElement, label, url) {
            const hiddenClass = 'tw-hidden';
            const hasValidData = label && url;

            if (hasValidData) {
                ctaElement.setAttribute('href', url);
                ctaElement.setAttribute('title', label);
                ctaElement.innerHTML = label;
                ctaElement.classList.remove(hiddenClass);
            } else {
                ctaElement.classList.add(hiddenClass);
            }
        }

        // Update BoundingArea and BackgroundPath on resize
        handleResize() {
            if (!this.rtrViewer) return;

            if (window.innerWidth >= 1024) {
                // Desktop BoundingArea -> più a sinistra
                this.rtrViewer.setBoundingArea({
                    topLeft: { x: this.desktopBoundLeft, y: 0 },   // <— 0.35 di default (prima era 0.5)
                    bottomRight: { x: this.desktopBoundRight, y: 1 },
                });

                // Desktop BackgroundPath
                this.rtrViewer.setBackgroundPath(this.desktopBackground || this.defaultBackground);
            } else {
                // Mobile BoundingArea (full width di default)
                this.rtrViewer.setBoundingArea({
                    topLeft: { x: this.mobileBoundLeft, y: 0 },
                    bottomRight: { x: this.mobileBoundRight, y: 1 },
                });

                // Mobile BackgroundPath
                this.rtrViewer.setBackgroundPath(this.mobileBackground || this.defaultBackground);
            }
        }
    }

    class PoRTRBox extends HTMLElement {
        constructor() {
            super();
            
            // Container
            this.sectionId = this.dataset.sectionId;
            this.container = this.querySelector(`#rtr-container-${this.sectionId}`);
        
            // Changers
            this.rtrChangers = this.querySelectorAll('.rtr-changer');
            if (this.rtrChangers.length > 1) {
                this.defaultProduct = Array.from(this.rtrChangers).find(input => input.checked);
                if (this.defaultProduct) {
                    this.defaultProductUpc = this.defaultProduct.value;
                }
            
                this.rtrChangers.forEach((input) => input.addEventListener('change', this.handleChange.bind(this)));
            } else if (this.rtrChangers.length === 1) {
                this.defaultProductUpc = this.rtrChangers[0].value;
            }

            this.swiperElement = this.querySelector(`.swiper-${this.sectionId}`);
        
            // Resize event listener
            this.handleResize = this.handleResize.bind(this);
            window.addEventListener('resize', this.handleResize);
            
            // Init object with settings
            this.initInput = {
            data: {
                selector: `#${this.container.id}`,
                id: {
                type: 'upc', // moco or token
                value: `${this.defaultProductUpc}`, // or any other valid UPC, MoCo or token
                },
                settings: {
                autoResetCamera: true,
                autoRotateCamera: false,
                autoRotateSpeed: 20,
                backgroundFit: 'fill',
                boundingArea: {
                    topLeft: {
                    x: 0,
                    y: 0,
                    },
                    bottomRight: {
                    x: 1,
                    y: 1,
                    },
                },
                boundingFit: 'auto',
                cameraAnimationTime: 0.3,
                cameraRotationInitial: {
                    phi: 0,
                    theta: 0,
                },
                cameraRotationReset: {
                    phi: 0,
                    theta: 0,
                },
                clearColor: '#ffffff',
                gestures: {
                    mouse: {
                    left: 'rotate',
                    middle: 'none',
                    right: 'pan',
                    wheel: 'none',
                    },
                    touches: {
                    one: 'rotate',
                    two: 'dolly-pan',
                    three: 'none',
                    },
                },
                lensesName: undefined,
                lensesOpacity: 1,
                orbitPoint: true,
                qualityMoving: 4,
                qualityStill: 5,
                scrollInteraction: false,
                showBackground: false,
                showEnvironment: false,
                transmission: true,
                }
            },
            callbacks: {
                onRendered: () => this.handleOnRendered()
            },
            };
        }
    
        connectedCallback() {
            window.addEventListener('DOMContentLoaded', () => {
                const rtrViewer = window.rtrViewer;
                rtrViewer.init(this.initInput);
      
                if (this.rtrChangers.length > 1) {
                    this.swiper = new Swiper(this.swiperElement, {
                        slidesPerView: 'auto',
                        slidesPerGroupAuto: true,
                        spaceBetween: 16,
                        updateOnWindowResize: true,
                        loop: false,
                        grabCursor: true
                    });
                }
            });
        }
    
        handleOnRendered() {
            if (this.rtrChangers.length > 1) {
                this.rtrChangers.forEach((input) => {
                    if (input.disabled) {
                        input.removeAttribute('disabled');
                    }
                });
            }
        }
    
        handleChange(event) {
            const selectedValue = event.target.value;
            // Disable Changers to avoid more than one switch at a time before rendering
            if (this.rtrChangers.length > 1) {
                this.rtrChangers.forEach((input) => {
                    if (!input.disabled) {
                        //input.setAttribute('disabled', '');
                        input.removeAttribute('checked');
                    }
                });
            }

            event.target.setAttribute('checked', '');

            // Update upc ID
            if (selectedValue) {
                rtrViewer.setId({
                    type: 'upc',
                    value: `${event.target.value}`,
                });
            }
        }
    
      // Update BoundingArea and BackgroundPath on resize
        handleResize() {
            if (window.innerWidth >= 1024) {
                // Desktop BoundingArea
                rtrViewer.setBoundingArea({
                    topLeft: {
                    x: 0,
                    y: 0,
                    },
                    bottomRight: {
                    x: 1,
                    y: 1,
                    },
                });
            } else {
                // Mobile BoundingArea
                rtrViewer.setBoundingArea({
                    topLeft: {
                    x: 0,
                    y: 0,
                    },
                    bottomRight: {
                    x: 1,
                    y: 1,
                    },
                });
            }
        }
    }

    class PoBackBtn extends HTMLElement {
        connectedCallback() {
            this.parentHref = this.dataset.parentHref;
            this.addEventListener("click", this.handleClick);
        }

        disconnectedCallback() {
            this.removeEventListener("click", this.handleClick);
        }

        handleClick = () => {
            if (document.referrer?.startsWith(window.location.origin) && document.referrer !== window.location.href) {
                document.location.href = document.referrer;
                return;
            }

            if (this.parentHref) {
                document.location.href = this.parentHref;
            }
        }
    }

    class PoDynamicProductCard extends HTMLElement {

        static get observedAttributes() {
            return ['data-product-handle', 'data-product-url', 'data-product-template', 'data-section-id'];
        }

        static getSharedState() {
            if (!window.__poDynamicProductCardState) {
                window.__poDynamicProductCardState = {
                    activeCount: 0,
                    maxConcurrent: 3,
                    maxCacheEntries: 24,
                    markupCache: new Map(),
                    inFlightRequests: new Map(),
                    queue: [],
                };
            }

            return window.__poDynamicProductCardState;
        }

        static getCachedMarkup(cacheKey) {
            const state = this.getSharedState();
            if (!state.markupCache.has(cacheKey)) return "";

            const markup = state.markupCache.get(cacheKey) || "";
            state.markupCache.delete(cacheKey);
            state.markupCache.set(cacheKey, markup);
            return markup;
        }

        static setCachedMarkup(cacheKey, markup) {
            if (!cacheKey || !markup) return markup;

            const state = this.getSharedState();
            if (state.markupCache.has(cacheKey)) {
                state.markupCache.delete(cacheKey);
            }

            state.markupCache.set(cacheKey, markup);

            while (state.markupCache.size > state.maxCacheEntries) {
                const oldestKey = state.markupCache.keys().next().value;
                if (!oldestKey) break;
                state.markupCache.delete(oldestKey);
            }

            return markup;
        }

        static enqueueRequest(task) {
            const state = this.getSharedState();

            return new Promise((resolve, reject) => {
                state.queue.push({ task, resolve, reject });
                this.flushQueue();
            });
        }

        static flushQueue() {
            const state = this.getSharedState();

            while (state.activeCount < state.maxConcurrent && state.queue.length > 0) {
                const nextRequest = state.queue.shift();
                if (!nextRequest) break;

                state.activeCount += 1;

                Promise.resolve()
                    .then(nextRequest.task)
                    .then(nextRequest.resolve)
                    .catch(nextRequest.reject)
                    .finally(() => {
                        state.activeCount = Math.max(0, state.activeCount - 1);
                        this.flushQueue();
                    });
            }
        }

        static async fetchCardMarkup(url, cardSelector) {
            const cacheKey = `${cardSelector}::${url}`;
            const cachedMarkup = this.getCachedMarkup(cacheKey);
            if (cachedMarkup) {
                return cachedMarkup;
            }

            const state = this.getSharedState();
            if (state.inFlightRequests.has(cacheKey)) {
                return state.inFlightRequests.get(cacheKey);
            }

            const request = this.enqueueRequest(async () => {
                const response = await fetch(url);
                if (!response.ok) {
                    return "";
                }

                const text = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, 'text/html');
                const cardElement = doc.querySelector(cardSelector);
                const normalizedCardElement = cardElement?.id === 'po-collection-product-card'
                    ? cardElement.firstElementChild
                    : cardElement;
                const hasContent = normalizedCardElement
                    && (normalizedCardElement.firstElementChild || normalizedCardElement.textContent.trim() !== '');

                if (!hasContent) {
                    return "";
                }

                return this.setCachedMarkup(cacheKey, normalizedCardElement.outerHTML);
            }).finally(() => {
                state.inFlightRequests.delete(cacheKey);
            });

            state.inFlightRequests.set(cacheKey, request);
            return request;
        }

        normalizeHandle(handle = "") {
            const cleaned = (handle || "").toString().trim().replace(/^\/+|\/+$/g, '').split(/[?#]/)[0];
            if (!cleaned || cleaned === "undefined" || cleaned === "null") return "";
            return cleaned.toLowerCase();
        }

        buildHandleCandidates(rawHandle) {
            const normalized = this.normalizeHandle(rawHandle);
            if (!normalized) return [];

            const hyphenated = normalized
                .replace(/[_\\s]+/g, "-")
                .replace(/--+/g, "-")
                .replace(/^-+|-+$/g, "");

            const candidates = [normalized];
            if (hyphenated && hyphenated !== normalized) candidates.push(hyphenated);
            return [...new Set(candidates)].filter(Boolean);
        }

        resolveProductHandle(candidate) {
            const directHandles = this.buildHandleCandidates(candidate || this.dataset.productHandle);
            const productUrl = this.dataset.productUrl;
            const urlHandles = (() => {
                if (!productUrl) return [];
                try {
                    const url = new URL(productUrl, window.location.origin);
                    const match = url.pathname.match(/\/products\/([^/]+)/);
                    return match?.[1] ? this.buildHandleCandidates(match[1]) : [];
                } catch (_e) {
                    return [];
                }
            })();

            const candidates = [...new Set([...directHandles, ...urlHandles])];
            this.productHandleCandidates = candidates;
            this.currentHandleIdx = 0;
            this.productHandle = candidates[0] || "";
            return this.productHandle;
        }

        resolveProductTemplate() {
            this.productTemplate = this.dataset.productTemplate || this.productTemplate || "po-product-card";
            return this.productTemplate;
        }

        resolveSectionId() {
            this.sectionId = this.dataset.sectionId || "";
            return this.sectionId;
        }

        getLoadingSkeletonMarkup() {
            return `
                <div class="po-card-fetch-skeleton" aria-hidden="true">
                    <div class="po-card-fetch-skeleton-media"></div>
                    <div class="po-card-fetch-skeleton-content">
                        <div class="po-card-fetch-skeleton-line po-card-fetch-skeleton-line--title"></div>
                        <div class="po-card-fetch-skeleton-line po-card-fetch-skeleton-line--price"></div>
                        <div class="po-card-fetch-skeleton-line po-card-fetch-skeleton-line--caption"></div>
                    </div>
                </div>
            `;
        }

        applyCardUrlParams(url) {
            if (!url?.searchParams) return url;

            if (this.dataset.productTile) {
                url.searchParams.set("product_tile", this.dataset.productTile);
            }
            if (this.dataset.hasBackground) {
                url.searchParams.set("has_background", this.dataset.hasBackground);
            }
            if (this.dataset.outputOption) {
                url.searchParams.set("output_option", this.dataset.outputOption);
            }
            if (this.dataset.optionSource) {
                url.searchParams.set("option_source", this.dataset.optionSource);
            }
            if (this.dataset.animateThumbs) {
                url.searchParams.set("animate_thumbs", this.dataset.animateThumbs);
            }
            if (this.dataset.showVariant) {
                url.searchParams.set("show_variant", this.dataset.showVariant);
            }

            return url;
        }

        buildProductCardUrls() {
            const urls = [];
            const template = this.productTemplate;
            const handles = Array.isArray(this.productHandleCandidates) ? this.productHandleCandidates : [this.productHandle];
            const productUrl = this.dataset.productUrl;

            if (this.sectionId) {
                handles.forEach((handle) => {
                    if (!handle) return;

                    const sectionUrl = new URL(Shopify.routes.root, window.location.origin);
                    sectionUrl.searchParams.set("section_id", this.sectionId);
                    sectionUrl.searchParams.set("product_handle", handle);
                    urls.push(this.applyCardUrlParams(sectionUrl).toString());
                });
            }

            if (productUrl) {
                try {
                    const url = new URL(productUrl, window.location.origin);
                    url.searchParams.set("view", template);
                    urls.push(this.applyCardUrlParams(url).toString());
                } catch (_e) {
                    /* ignore malformed url */
                }
            }

            handles.forEach((handle) => {
                if (!handle) return;
                const url = new URL(`${Shopify.routes.root}products/${encodeURIComponent(handle)}`, window.location.origin);
                url.searchParams.set("view", template);
                urls.push(this.applyCardUrlParams(url).toString());
            });

            return [...new Set(urls)].filter(Boolean);
        }

        attributeChangedCallback(name, oldValue, newValue) {
            if (oldValue != newValue && this.isConnected) {
                this.resolveSectionId();
                this.resolveProductTemplate();
                this.productHandle = this.resolveProductHandle(
                    name === 'data-product-handle' ? newValue : this.dataset.productHandle
                );
                if (!this.productHandle || (!this.productTemplate && !this.sectionId)) return;
                this.cardFetched = false;
                this.scheduleFetchProductCard(true);
            }
        }

        connectedCallback() {
            this._fetchRequestId = this._fetchRequestId || 0;
            this._visibilityObserver = this._visibilityObserver || null;
            this.resolveSectionId();
            this.resolveProductTemplate();
            this.productHandle = this.resolveProductHandle();
            if (!this.productHandle) return;
            this.cardFetched = false;
            this.showOnLoad = this.dataset.showOnLoad === "true";
            this.innerHTML = this.getLoadingSkeletonMarkup();
            this.scheduleFetchProductCard();
        }

        disconnectedCallback() {
            if (this._visibilityObserver) {
                this._visibilityObserver.disconnect();
                this._visibilityObserver = null;
            }

            this._fetchRequestId = (this._fetchRequestId || 0) + 1;
        }

        isNearViewport() {
            const rect = this.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;

            return (
                rect.width > 0 &&
                rect.height > 0 &&
                rect.bottom >= -200 &&
                rect.right >= 0 &&
                rect.top <= viewportHeight + 250 &&
                rect.left <= viewportWidth
            );
        }

        scheduleFetchProductCard(forceImmediate = false) {
            if (this.cardFetched) return;

            if (this._visibilityObserver) {
                this._visibilityObserver.disconnect();
                this._visibilityObserver = null;
            }

            if (forceImmediate || this.showOnLoad || !('IntersectionObserver' in window) || this.isNearViewport()) {
                this.fetchProductCard();
                return;
            }

            this._visibilityObserver = new IntersectionObserver((entries, observer) => {
                const visibleEntry = entries.find((entry) => entry.isIntersecting || entry.intersectionRatio > 0);
                if (!visibleEntry) return;

                observer.disconnect();
                this._visibilityObserver = null;
                this.fetchProductCard();
            }, {
                rootMargin: "250px 0px",
                threshold: 0.01,
            });

            this._visibilityObserver.observe(this);
        }

        async fetchProductCard() {
            const urls = this.buildProductCardUrls();
            if (!urls.length) return;

            this._fetchRequestId = (this._fetchRequestId || 0) + 1;
            const requestId = this._fetchRequestId;

            this.toggleAttribute("data-loaded", false);
            this.innerHTML = this.getLoadingSkeletonMarkup();

            const cardSelector = this.dataset.cardSelector || '#po-collection-product-card';

            for (const url of urls) {
                this.productCardFetchUrl = url;

                try {
                    if (requestId !== this._fetchRequestId) return;
                    const cardMarkup = await this.constructor.fetchCardMarkup(url, cardSelector);
                    if (!cardMarkup || requestId !== this._fetchRequestId) continue;

                    const template = document.createElement('template');
                    template.innerHTML = cardMarkup.trim();
                    const normalizedCardElement = template.content.firstElementChild;
                    if (!normalizedCardElement) continue;

                    this.innerHTML = "";
                    this.append(normalizedCardElement);
                    this.cardFetched = true;
                    this.toggleAttribute("data-loaded", true);

                    if (this.showOnLoad) {
                        this.style.display = "block";
                    }
                    document.dispatchEvent(new CustomEvent('po:dpc:loaded', { detail: { handle: this.productHandle } }));
                    return;
                } catch (error) {
                    console.error('Error fetching product card:', error);
                }
            }

            if (requestId === this._fetchRequestId) {
                this.remove();
            }
        }
    }

    // eslint-disable-next-line no-unused-vars
    class VMMVPdp extends HTMLElement {
        constructor() {
            super();

            // Container
            this.container = this.querySelector(`.vmmv-container`);
            this.buttonsTryOn = document.querySelectorAll('[data-button-tryon]');
            this.updSelected = this.container.dataset.upcSelected;
            this.logoUrl = this.container?.dataset.logoUrl || '';
            this.currencySymbol = this.container?.dataset.currencySymbol || '';

            this.prodList = [];
            this.upcList = [];
            this.onlyUpcList = [];
            this.wishlist = [];
            this.testWishlist = [];
            this.prices = [];
            this.prodIdVariantId = [];
            this.resultTransitions = [];
            // uat key
            // this.VMInitParamsKey = 'dc9ab158-7aa6-4ef2-bcbb-fad4a0591d4a'
            // prod key
            this.VMInitParamsKey = '021c79f6-8685-4e78-a5bb-8e7f47cab64b'

            // uat params
            // 'source': 'VMMV_PO',

            this.VMInitParams = {
                'source': 'VMMV_PERSOL',
                'key': this.VMInitParamsKey,
                'channel': 'ecommerce',
                'brand': 'po',
            };

            this.store = {
                'id': '',
                'type': '',
            };

            if (this.container) {
                this.upcList = this.container.dataset.upcList.split('||');
                this.upcList.forEach(stringUpc => {
                    this.dettUpc = stringUpc.split('#_#');
                    this.brand = {
                        'name': this.dettUpc[5],
                        'logoUrl': this.dettUpc[6]
                    };
                    this.onlyUpcList.push(this.dettUpc[0]);

                    this.prodIdVariantId[this.dettUpc[1]] = {
                        'product': this.dettUpc[13],
                        'handle': this.dettUpc[14]
                    };

                    this.testWishlist[this.dettUpc[1]] = {
                        'product_id': this.dettUpc[13],
                        'variant_id': this.dettUpc[1],
                        'handle': this.dettUpc[14],
                        'upc': this.dettUpc[0],
                    }
                });
            }
        }

        connectedCallback() {
            // Keep CTA hidden until live availability check confirms support
            this.buttonsTryOn.forEach((btn) => {
                btn.classList.add('tw-hidden');
                btn.setAttribute('aria-disabled', 'true');
            });

            const handleGraphLoaded = (data) => {
                const newRecords = this.buildUpcRecordsFromGraph(data.products);
                if (newRecords.length) {
                    this.upcList.push(...newRecords);
                    this.checkUpcAvailability(this.onlyUpcList, this.updSelected).then(result => {
                        this.resultTransitions = result;
                    });
                }
            };

            if (window.variantsHandleGraphData?.loaded) {
                handleGraphLoaded(window.variantsHandleGraphData);
            }

            window.addEventListener('variantsHandleGraphLoaded', (event) => {
                handleGraphLoaded(event.detail);
            });

            this.checkUpcAvailability(this.onlyUpcList, this.updSelected).then(result => {
                this.resultTransitions = result;
            });

            this.tryOnUnsubscriber = subscribe(PUB_SUB_EVENTS.startTryOn, (event) => {
                if (event.source != "try-on-pdp") return;

                if (this.container && this.prodList.length === 0) {
                    this.upcList.forEach(stringUpc => {
                    this.dettUpc = stringUpc.split('#_#');
                    if (this.resultTransitions[this.dettUpc[0]]?.available == true) {
                        let prodItem = {
                        'upc': this.dettUpc[0],
                        'code': this.dettUpc[1],
                        'name': this.dettUpc[2],
                        'colorCode': this.dettUpc[3],
                        'thumbnailUrl': this.dettUpc[4],
                        'brand': this.brand,
                        'size': this.dettUpc[9],
                        'lensColorLabel': this.dettUpc[8],
                        'frameColorLabel': this.dettUpc[7]
                        };

                        if (this.resultTransitions[this.dettUpc[0]].transitions == true) {
                        prodItem.isTransition = true;
                        prodItem.transitionLens = {
                            defaultColor: 'SIGNATURE-GEN8-AMBER',
                            defaultType: 'SIGNATURE',
                            defaultAlphaLevel: 0.5,
                            availableColors: [
                            'SIGNATURE-GEN8-AMBER',
                            'SIGNATURE-GEN8-AMETHYST',
                            'SIGNATURE-GEN8-EMERALD',
                            'SIGNATURE-GEN8-GRAY',
                            'SIGNATURE-GEN8-SAPPHIRE',
                            'SIGNATURE-GEN8-BROWN',
                            'SIGNATURE-GEN8-GGREEN'
                            ]
                        };
                        }

                        this.prodList.push(prodItem);
                        this.prices.push({
                        upc: this.dettUpc[0],
                        currency: this.dettUpc[12],
                        current: { text: this.dettUpc[10], value: parseFloat(this.dettUpc[10]) },
                        ...(this.dettUpc[10] != this.dettUpc[11] && this.dettUpc[11] > 0 ? {
                            previous: { text: this.dettUpc[11], value: parseFloat(this.dettUpc[11]) }
                        } : {})
                        });
                    }
                });
            }

            let localeWithCountry = this.getLocaleWithCountry();
            if (!localeWithCountry && window.SHOP_LOCALES?.current?.iso_code) {
                localeWithCountry = window.SHOP_LOCALES.current.iso_code;
            }
            if (localeWithCountry && (localeWithCountry.length <= 2 || !localeWithCountry.includes('-'))) {
                const rootUrl = window.SHOP_LOCALES?.current?.root_url;
                if (rootUrl && typeof rootUrl === 'string') {
                    const parts = rootUrl.replace(/^\/+/, '').split('-');
                    if (parts.length >= 2) {
                        localeWithCountry = `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`;
                    }
                }
            }
            if (!localeWithCountry) {
                localeWithCountry = 'en-GB';
            }
            const vmmvRegion = (localeWithCountry === 'en-US' || localeWithCountry === 'es-US') ? 'US'
                : (localeWithCountry === 'en-AU' || localeWithCountry === 'en-NZ') ? 'AU'
                : 'EU';

            this.initInput = {
                'selector': '.vmmv-container',
                'locale': localeWithCountry,
                'products': this.prodList,
                'store': { ...this.store, region: vmmvRegion },
                'prices': this.prices,
                'vmInit': { ...this.VMInitParams, 'region': vmmvRegion, 'REGION': vmmvRegion },
                'style': '',
                fromStore: true,
                isTryOnEnabled: true,
                isPictureModeEnabled: true,
                isUploadPictureEnabled: true,
                isVideoModeEnabled: true,
                isTakeScreenshotEnabled: true
            };

            let elem = document.querySelector('po-pdp-vmmv');
            if (elem) {
                elem.classList.remove('tw-hidden');
                elem.style.background = "var(--Alpha-Black-Dark, rgba(0, 0, 0, 0.50))";
                elem.style.backdropFilter = "blur(11px)";
                document.querySelector('body').style.overflowY = "hidden";
            }

            let vmapp = null;

            Object.values(this.testWishlist).forEach(async variantWish => {
                const isInWishlist = await PoWishlistUtils.isItemInWishlist(variantWish['variant_id']);
                if (isInWishlist) { this.wishlist.push(variantWish['upc']); }
            });

            const config = {
                ...this.initInput,
                transitionFeature: { enabled: true },
                onClose: async () => {
                if (elem) {
                    elem.classList.add('tw-hidden');
                    elem.style.background = "";
                    elem.style.backdropFilter = "";
                }
                await vmapp?.unmount();

                // Remove events subscriptions
                if (this.startTryOnUnsub) this.startTryOnUnsub();
                if (this.cartUpdateUnsub) this.cartUpdateUnsub();
                if (this.cartErrorUnsub) this.cartErrorUnsub();

                document.querySelector('body').style.overflowY = "";
                this.container.innerHTML = '';
                },
                onPrivacyReject: async () => {
                    await config.onClose();
                    await vmapp?.unmount();
                },
                onAddToBag: async (product, frameSize) => {
                    const vmmvProductContext = this.prodIdVariantId?.[product.code] || {};
                    const vmmvCurrentPrice = product.price?.current?.value ?? product.price?.amount ?? (
                        typeof product.price === 'number' || typeof product.price === 'string' ? product.price : ''
                    );
                    const vmmvFullPrice = product.price?.previous?.value ?? product.compareAtPrice?.amount ?? (
                        typeof product.compareAtPrice === 'number' || typeof product.compareAtPrice === 'string' ? product.compareAtPrice : vmmvCurrentPrice
                    );
                    const vmmvProductData = {
                        Status: product.isProductNotAvailable ? 'Out-of-stock' : 'Available',
                        Price: vmmvCurrentPrice,
                        PriceFull: vmmvFullPrice,
                        LensType: 'PLANO',
                        Upc: product.upc || product.sku || '',
                        Handle: vmmvProductContext.handle || product.handle || '',
                        ModelName: product.name || product.styleName || '',
                        Category: product.category || ''
                    };

                    publish(PUB_SUB_EVENTS.VMMVAddToCart, {
                      productVariantId: product.code,
                      productId: vmmvProductContext.product || product.productId || '',
                      productHandle: vmmvProductContext.handle || product.handle || '',
                      variantSku: product.upc || product.sku || product.code,
                      modelName: product.name || product.styleName || '',
                      category: product.category || '',
                      productData: vmmvProductData
                    });
                    
                    // Close VMMV after adding to cart listening to cart update event
                    this.cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
                      await config.onClose();
                    });
                    // Close VMMV if error occurs during add to cart listening to cart error event
                    this.cartErrorUnsub = subscribe(PUB_SUB_EVENTS.cartError, async (event) => {
                      await config.onClose();
                    });
                    
                },
                wishlistedUpcs: this.wishlist,
                onToggleWishlist: async (product) => {
                    const wlItem = {
                        productId: this.prodIdVariantId[product.code]['product'],
                        productHandle: this.prodIdVariantId[product.code]['handle'],
                        variantId: product.code
                    }
                    const inWl = await PoWishlistUtils.isItemInWishlist(product.code)
                    await PoWishlistUtils.updateWishlist(inWl ? WISHLIST_CONSTANTS.eventKeyRemove : WISHLIST_CONSTANTS.eventKeyAdd, wlItem)
                }
            };

            (async () => {
                const api = await this.waitForVmmv();
                vmapp = new api.VMWidgetApp();
                console.log('vmapp', vmapp);
                api.warmUp();
                await vmapp.setConfig(config);
                const vmInitWithRegion = { ...this.VMInitParams, 'region': vmmvRegion, 'REGION': vmmvRegion };
                // print all the config for debug
                console.log('Initializing VMMV with config:', {
                    ...vmInitWithRegion,
                });
                await vmapp.init(vmInitWithRegion);
            })().catch(e => console.error(e));
            });
        }

        /**
         * Transforms GraphQL product objects into UPC record strings (`#_#`-delimited).
         * Skips variants whose barcode is already present in `this.onlyUpcList`.
         * Side-effects: populates `onlyUpcList`, `prodIdVariantId`, and `testWishlist`.
         * @param {Object[]} products - Product objects from the Storefront API.
         * @returns {string[]} New `#_#`-delimited record strings.
         */
        buildUpcRecordsFromGraph(products) {
            const records = [];

            for (const product of products) {
                const productId = product.id.split('/').pop();
                const colorCode = product.colorCode?.value || '';
                const brandline = product.brandline?.value || '';
                const color = product.color?.value || '';
                const lensColorDescription = product.lensColorDescription?.value || '';
                const imageUrl = product.featuredImage?.url || '';

                for (const variant of product.variants.nodes) {
                    const barcode = variant.barcode;
                    if (!barcode || this.onlyUpcList.includes(barcode)) continue;

                    const variantId = variant.id.split('/').pop();
                    const lensWidth = variant.lensWidth?.value || '';
                    const bridgeWidth = variant.bridgeWidth?.value || '';
                    const price = variant.price?.amount || '0';
                    const compareAtPrice = variant.compareAtPrice?.amount || '0';

                    const record = [
                        barcode,
                        variantId,
                        product.title,
                        colorCode,
                        imageUrl,
                        brandline,
                        this.logoUrl,
                        color,
                        lensColorDescription,
                        `${lensWidth}-${bridgeWidth}`,
                        price,
                        compareAtPrice,
                        this.currencySymbol,
                        productId,
                        product.handle
                    ].join('#_#');

                    records.push(record);
                    this.onlyUpcList.push(barcode);

                    this.prodIdVariantId[variantId] = {
                        'product': productId,
                        'handle': product.handle
                    };

                    this.testWishlist[variantId] = {
                        'product_id': productId,
                        'variant_id': variantId,
                        'handle': product.handle,
                        'upc': barcode
                    };
                }
            }

            return records;
        }

        async checkUpcAvailability (upcs, upcSelected) {
            const api = await this.waitForVmmv();
            let resultTransitions = [];
            let upcAvailability   = await api.isUPCSupported(upcs, this.VMInitParamsKey);
            let isBrowserSupported = await api.isBrowserSupported();
            let browserSupported   = isBrowserSupported ? 1 : 0;
            let UpcSupported = 0;

            let anyUpcAvailable = false;
            Object.entries(upcAvailability).forEach(([upc, upcAvailabilityResponse]) => {
                resultTransitions[upc] = {
                    transitions: upcAvailabilityResponse.transitions,
                    available: upcAvailabilityResponse.available
                };
                if (upcAvailabilityResponse.available === true) {
                    anyUpcAvailable = true;
                    if (upc === upcSelected) UpcSupported = 1;
                }
            });
            if (anyUpcAvailable) UpcSupported = 1;
            if (anyUpcAvailable && browserSupported === 1) {
                this.buttonsTryOn.forEach((btn) => {
                    btn.classList.remove('tw-hidden');
                    btn.removeAttribute('aria-disabled');
                });
            } else {
                this.buttonsTryOn.forEach((btn) => {
                    btn.classList.add('tw-hidden');
                    btn.setAttribute('aria-disabled', 'true');
                });
            }

            publish(PUB_SUB_EVENTS.VMMVAvailability, {
                'Vm_IsUpcSupported': String(UpcSupported),
                'Vm_IsBrowserSupported': String(browserSupported),
                'Vm_PageContext': 'PDP',
            });

            return resultTransitions;
        }

        /**
         * Maps store region to VMMV REGION parameter (EU | US | AU) for banner/compliance.
         * Uses SHOP_LOCALES/URL so it matches the storefront also when PERSOL_STORE_REGION is not yet set.
         * @returns {string} 'EU' | 'US' | 'AU'
         */
        getVmmvRegion() {
            const win = typeof window !== 'undefined' ? window : null;
            if (!win) return 'EU';

            // Prefer locale from URL (SHOP_LOCALES) so VMMV banner matches the storefront
            let storeRegion = win.PERSOL_STORE_REGION;
            if (!storeRegion && win.SHOP_LOCALES && win.SHOP_LOCALES.current && win.SHOP_LOCALES.current.root_url) {
                const rootUrl = (win.SHOP_LOCALES.current.root_url || '').toLowerCase().trim();
                if (rootUrl === '/en-us') storeRegion = 'US';
                else if (rootUrl === '/en-ca' || rootUrl === '/fr-ca') storeRegion = 'CA';
                else if (rootUrl === '/en-au') storeRegion = 'AU';
                else if (rootUrl && rootUrl.length > 1) storeRegion = 'EMEA'; // /it-it, /en-gb, /de-de, etc.
            }
            if (!storeRegion && win.location && win.location.pathname) {
                const seg = win.location.pathname.split('/').filter(Boolean)[0] || '';
                const m = seg.match(/^([a-z]{2})[-_]([a-z]{2})$/i);
                if (m) {
                    const country = (m[2] || '').toUpperCase();
                    if (country === 'US') storeRegion = 'US';
                    else if (country === 'CA') storeRegion = 'CA';
                    else if (country === 'AU') storeRegion = 'AU';
                    else storeRegion = 'EMEA';
                }
            }
            if (!storeRegion) storeRegion = 'DEFAULT';

            switch (storeRegion) {
                case 'US':
                case 'CA':
                    return 'US';
                case 'EMEA':
                    return 'EU';
                case 'AU':
                    return 'AU';
                default:
                    return 'EU';
            }
        }

        getLocaleWithCountry() {
            const locale = (Shopify && Shopify.locale) ? Shopify.locale : '';
            const country = (Shopify && Shopify.country) ? Shopify.country : '';

            if (!locale && !country) return '';
            if (!locale) return country;
            if (!country) return locale;

            const normalizedCountry = country.toUpperCase();
            const localeParts = locale.split('-');
            const lastPart = localeParts[localeParts.length - 1] || '';

            return lastPart.toUpperCase() === normalizedCountry ? locale : `${locale}-${normalizedCountry}`;
        }

        waitForVmmv(timeout = 10000) {
            return new Promise((resolve, reject) => {
            const t0 = Date.now();
            (function tick() {
                const api = window.vmmv || window.VMMV;
                if (api && typeof api.isUPCSupported === 'function') return resolve(api);
                if (Date.now() - t0 > timeout) return reject(new Error('vmmv not ready'));
                requestAnimationFrame(tick);
            })();
            });
        }
    }
    class OpTryOnButton extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            // Assicuriamoci che il pulsante sia cliccabile
            this.style.cursor = 'pointer';
            this.setAttribute('role', 'button');
            this.setAttribute('tabindex', '0');
            
            // Aggiungiamo il gestore di eventi sia per click che per keydown (accessibilità)
            this.addEventListener('click', this.handleActivation);
            this.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.handleActivation();
                }
            });
        }
        
        handleActivation = () => {
            console.log('Try On button clicked');

            const elementId = this.dataset.elementId || this.getAttribute('data-element-id');
            if (elementId && !this.matches('button,a')) {
                const tealiumEvent = { id: 'Click', 'data_element_id': elementId };
                if (typeof window.sendTealiumEvent === 'function') {
                    window.sendTealiumEvent(tealiumEvent);
                } else if (typeof window.tealium_data2track !== 'undefined' && typeof window.tealium_data2track.push === 'function') {
                    window.tealium_data2track.push(tealiumEvent);
                }
            }
            
            // Se la funzione publish è disponibile, usala
            if (typeof publish === 'function') {
                publish(PUB_SUB_EVENTS.startTryOn, {
                    source: 'try-on-pdp'
                });
            } else {
                // Altrimenti, usiamo un evento personalizzato
                const event = new CustomEvent(PUB_SUB_EVENTS.startTryOn, {
                    detail: {
                        source: 'try-on-pdp'
                    },
                    bubbles: true
                });
                this.dispatchEvent(event);
                
                // E mostriamo direttamente il container VMMV
                const vmmvContainer = document.querySelector('po-pdp-vmmv');
                if (vmmvContainer) {
                    vmmvContainer.classList.remove('tw-hidden');
                    // Imposta lo sfondo per migliorare la visibilità
                    vmmvContainer.style.background = "rgba(0, 0, 0, 0.5)";
                    vmmvContainer.style.backdropFilter = "blur(11px)";
                    document.querySelector('body').style.overflowY = "hidden";
                }
            }
        }
    }

    class OpVtoTrigger extends HTMLElement {
        connectedCallback() {
            this.ctaBtn = this.querySelector('.po-banner-cta');
            if (this.ctaBtn) {
                this.ctaBtn.addEventListener('click', this.handleActivation);
                this.ctaBtn.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.handleActivation(e);
                    }
                });
            }
        }

        handleActivation = (e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('VTO Trigger activated');

            // Scroll to top to ensure VTO container visibility
            window.scrollTo({ top: 0, behavior: 'smooth' });

            if (typeof publish === 'function') {
                publish(PUB_SUB_EVENTS.startTryOn, { source: 'try-on-pdp' });
            } else {
                const event = new CustomEvent(PUB_SUB_EVENTS.startTryOn || 'start-try-on', {
                    detail: { source: 'try-on-pdp' },
                    bubbles: true
                });
                this.dispatchEvent(event);
            }
        }
    }

    class PoStripNewsletter extends HTMLElement {

        constructor() {
            super();
            this.closeButtoon = this.querySelector('[data-close]');
            this.sectionSelecorId = this.dataset.sectionSelectorId;
            if(this.offsetHeight>0){
                this.style.marginBottom = `-${this.offsetHeight}px`;
            }
        }

        connectedCallback() {
            this.closeButtoon.addEventListener('click',(event)=>{
                this.classList.add('tw-hidden');
                if(this.offsetHeight>0){
                    this.style.marginBottom = `${this.offsetHeight}px`;
                }
            });

            const section = document.querySelector('#'+this.sectionSelecorId);
            let lastScrollY = window.scrollY;
            window.addEventListener('scroll', () => {
                if (window.scrollY > lastScrollY) {
                    section.classList.add('visible');
                } else {
                    section.classList.remove('visible');
                }
                lastScrollY = window.scrollY;
            });

        }
    }

    class PoDiscountNinjaPrice extends HTMLElement {
        connectedCallback() {
            window.opTealiumPrices = window.opTealiumPrices || [];
            this.pdHandle = this.dataset.laDnProductHandle;
            this.extractPrice();
        }

        async extractPrice() {
            let priceObj = {};

            await ObserverUtils.waitForElement('[data-limoniapps-discountninja-price-cents-presentmentcurrency]', this, 5000)
                .then(() => {
                    this.priceElements = this.querySelectorAll('[data-limoniapps-discountninja-price-cents-presentmentcurrency]');

                    // Extract prices from the elements
                    const prices = [...this.priceElements].map(el => {
                        const priceString = el.querySelector('.money').textContent.trim();
                        return GeneralUtils.parseCurrency(priceString);
                    });

                    priceObj = {
                        price: Math.min(prices[0]?.value, prices[1]?.value),
                        priceFull: Math.max(prices[0]?.value, prices[1]?.value)
                    };
                })
                .catch(() => {
                    const pdPriceFull = GeneralUtils.parseCurrency(this.dataset.laDnProductPrice).value;
                    priceObj =  {
                        price: pdPriceFull,
                        priceFull: pdPriceFull,
                    };
                })
                .finally(() => {
                    priceObj.productHandle = this.pdHandle;
                    if (!window.opTealiumPrices.find(item => item.productHandle === this.pdHandle)) window.opTealiumPrices.push(priceObj);
                });
        }
    }

    class PoMeasurementUpdater extends HTMLElement {
        constructor() {
            super();
            this.measurementElement = this.querySelector('[data-measurement-element]');
            this.measurementUnitElement = this.querySelector('[data-measurement-unit]');
            this.currentMeasurementUnit = 'mm';
            this.mmToInchFactor = 1 / 25.4;
        }

        connectedCallback() {
            subscribe(PUB_SUB_EVENTS.faceSizeSelection, (event) => {
               this.updateMeasurements(event);
            });

            window.addEventListener('po:measurement-toggle-changed', (event) => {
                const newUnit = event.detail.measureUnit;
                this.convertMeasurement(newUnit);
            });
        }

        // Convert measurement to the selected unit
        convertMeasurement(newUnit) {
            const mmValue = parseFloat(this.dataset.mmValue);            
            
            if (!isNaN(mmValue)) {
                this.currentMeasurementUnit = newUnit;
                this.displayMeasurement(mmValue);
            }
        }

        // Update measurement when variant changes
        updateMeasurements(event) {
            const variant = event.data.variant;
            const measurementType = this.dataset.measurementType;

            if (variant) {
                const newValue = variant[measurementType];
                if (typeof newValue !== 'undefined') {
                    // Store original MM value
                    this.dataset.mmValue = newValue;
                    // Display value based on current unit
                    this.displayMeasurement(newValue);
                }
            }
        }

        // Display measurement in the selected unit
        displayMeasurement(mmValue) {
            let displayValue;
            
            if (this.currentMeasurementUnit === 'inch') {
                displayValue = (mmValue * this.mmToInchFactor).toFixed(2);
            } else {
                displayValue = mmValue;
            }
            
            this.measurementElement.textContent = displayValue;
            
            if (this.measurementUnitElement) {
                this.measurementUnitElement.textContent = this.currentMeasurementUnit;
            }
        }
      
    }
    class PoMeasurementToggler extends HTMLElement {
        constructor() {
            super();
            this.measurementToggleInput = this.querySelector('[data-measurement-toggle]');
        }

        connectedCallback() {
            this.measurementToggleInput.addEventListener('change', event => {
                this.handleMeasurementToggle(event);                
            });
        }

        handleMeasurementToggle(event) {
          const measureUnit = event.target.checked ? 'inch' : 'mm';
    
          // Dispatch custom event that PoMeasurementUpdater can listen to
          window.dispatchEvent(new CustomEvent('po:measurement-toggle-changed', {
              detail: { measureUnit },
              bubbles: true
          }));
        }
    }

    class PoStickyBottomBar extends HTMLElement {
        constructor() {
            super();
            this.visibilityObserver = null;
            this.scrollTarget = null;
            this.currentPadding = 0;
        }
        
        connectedCallback() {
            document.addEventListener('DOMContentLoaded', () => {
                const targetId = this.dataset.scrollTarget; 
                if (!targetId) {
                    console.warn('PoStickyBottomBar: missing data-scroll-target attribute');
                    return;
                }

                this.scrollTarget = document.getElementById(targetId);
                if (!this.scrollTarget) {
                    console.warn(`PoStickyBottomBar: element with id "${targetId}" not found`);
                    return;
                }

                const style = window.getComputedStyle(this.scrollTarget);
                if (style.display === 'none' || style.visibility === 'hidden') return;

                this.setBodyPadding();
            
                this.observeScrollTarget();

            }, { once: true });
        }

        disconnectedCallback() {
            if (this.visibilityObserver) {
                this.visibilityObserver.disconnect();
                this.visibilityObserver = null;
            }

            document.body.style.paddingBottom = '';

            this.scrollTarget = null;
        }

        setBodyPadding() {
            if (!this.classList.contains('tw-hidden')) {
                const barHeight = this.offsetHeight;
                document.body.style.paddingBottom = `${barHeight}px`;
            }
        }

        observeScrollTarget() {
            this.visibilityObserver = new IntersectionObserver(
                this.onScrollTargetVisibilityChange.bind(this),
                { threshold: 0 }
            );

            this.visibilityObserver.observe(this.scrollTarget);
        }

        onScrollTargetVisibilityChange(entries) {
            const entry = entries[0];

            if (this.hasAttribute('style')) {
                this.removeAttribute('style');
            }

            const isTargetElementVisible = entry.isIntersecting;
            this.classList.toggle('tw-translate-y-full', isTargetElementVisible);   // if target is visible, hide the sticky bar
            this.classList.toggle('tw-translate-y-0', !isTargetElementVisible);     // if target is outside of the viewport, show it
        }
    }

    class ScrollToBtn extends HTMLElement {
      constructor() {
        super();
        this.scrollButton = this.querySelector('[data-scroll-to]');
        this.targetElement = null;
        this.handleClick = this.handleClick.bind(this);
      }

      connectedCallback() {
        if (!this.scrollButton) {
          console.warn('ScrollToBtn: Scroll button with data-scroll-to attribute not found.');
          return;
        }

        const selector = this.scrollButton.dataset.scrollTo;
        if (!selector) {
          console.warn('ScrollToBtn: data-scroll-to attribute is empty.');
          return;
        }

        // IMPORTANT: Since is a general selector, it could be an ID, class, or any valid selector.
        // So in the data-scroll-to attribute, the full selector must be provided (e.g., "#section-id" or ".section-class").
        this.targetElement = document.querySelector(selector);

        if (!this.targetElement) {
          console.warn('ScrollToBtn: Target element not found for:', selector);
          return;
        } else {
          this.scrollButton.classList.remove('js-no-scroll-target');
        }

        this.scrollButton.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        if (this.scrollButton) {
          this.scrollButton.removeEventListener('click', this.handleClick);
        }
      }

      handleClick(event) {
        event.preventDefault();

        this.targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        // Accessibility improvement
        this.targetElement.setAttribute('tabindex', '-1');
        this.targetElement.focus({ preventScroll: true });
        this.targetElement.addEventListener('blur', () => {
          this.targetElement.removeAttribute('tabindex');
        }, { once: true });
      }
    }


    function setupCustomElements () {
        if (!customElements.get('po-video-with-thumbnail')) {
            customElements.define("po-video-with-thumbnail", OpVideoWithThumbnail);
        }
    
        if (!customElements.get('po-form')) {
            customElements.define("po-form", OpForm);
        }
        
        if (!customElements.get('po-password-checker')) {
            customElements.define("po-password-checker", OpPasswordChecker);
        }

        if (!customElements.get('po-progress-indicator')) {
            customElements.define('po-progress-indicator', PoProgressIndicator);
        }
    
        if (!customElements.get('po-quantity-input')) {
            customElements.define("po-quantity-input", OpQuantitySelector);
        }
    
        if (!customElements.get("po-tabs")) {
            customElements.define("po-tabs", OpTabs);
        }
    
        if (!customElements.get("po-rangeslider")) {
            customElements.define("po-rangeslider", OpRangeSlider);
        }
    
        if (!customElements.get('po-text-field')) {
            customElements.define("po-text-field", OpTextField);
        }
    
        if (!customElements.get('po-slideshow')) {
            customElements.define("po-slideshow", OpSlideShow);
        }
    
        if (!customElements.get('po-countdown')) {
            customElements.define("po-countdown", PoCountdown);
        }

        if (!customElements.get("po-modal-trigger")) {
            customElements.define("po-modal-trigger", OpModalTrigger);
        }

        if (!customElements.get('po-slider-component')) {
            window.PoSliderComponent = PoSliderComponent;
            customElements.define('po-slider-component', PoSliderComponent);
        }

        if (!customElements.get('po-character-limiter')) {
            customElements.define("po-character-limiter", PoCharacterLimiter);
        }

        if (!customElements.get('po-swiper')) {
            customElements.define('po-swiper', PoSwiper);
        }

        if (!customElements.get('po-password-checker-container')) {
            customElements.define("po-password-checker-container", OpPasswordCheckerContainer);
        }

        if (!customElements.get("po-modal-closer")) {
            customElements.define("po-modal-closer", PoModalCloser);
        }

        if (!customElements.get('po-dialog')) {
            customElements.define("po-dialog", PoDialog);
        }

        if (!customElements.get("po-intersection-sticky")) {
            customElements.define("po-intersection-sticky", OpIntersectionSticky);
        }

        if (!customElements.get("po-delivery-estimator")) {
            customElements.define("po-delivery-estimator", PoDeliveryEstimator);
        }

        if (!customElements.get('po-scroll-progress')) {
            customElements.define("po-scroll-progress", OpScrollProgress);
        }
        
        if (!customElements.get("po-dark-toggle")) {
            customElements.define("po-dark-toggle", PoDarkToggle);
        }

        if (!customElements.get("po-rtr-viewer-list")) {
            customElements.define("po-rtr-viewer-list", PoRTRViewerList);
        }

        if (!customElements.get("po-rtr-box")) {
            customElements.define("po-rtr-box", PoRTRBox);
        }

        if (!customElements.get('po-dynamic-product-card')) {
            customElements.define("po-dynamic-product-card", PoDynamicProductCard);
        }
        
        if (!customElements.get("po-pdp-vmmv")) {
            // Assegniamo la classe a una variabile temporanea per "utilizzarla" esplicitamente
            const VMMVPdpComponent = VMMVPdp;
            customElements.define("po-pdp-vmmv", VMMVPdpComponent);
        }
        
        if (!customElements.get("po-try-on-button")) {
            customElements.define("po-try-on-button", OpTryOnButton);
            customElements.define("op-vto-trigger", OpVtoTrigger);

        }

        if (!customElements.get('po-strip-newsletter')) {
            customElements.define('po-strip-newsletter', PoStripNewsletter);
        }

        if (!customElements.get('po-back-btn')) {
            customElements.define("po-back-btn", PoBackBtn);
        }
        if (!customElements.get('po-discount-ninja-price')) {
            customElements.define('po-discount-ninja-price', PoDiscountNinjaPrice);
        }
        if (!customElements.get('po-measurements-updater')) {
            customElements.define('po-measurements-updater', PoMeasurementUpdater);
        }
        if (!customElements.get('po-measurements-toggler')) {
            customElements.define('po-measurements-toggler', PoMeasurementToggler);
        }
        if (!customElements.get('po-sticky-bottom-bar')) {
            customElements.define('po-sticky-bottom-bar', PoStickyBottomBar);
        }
        if (!customElements.get('scroll-to-btn')) {
            customElements.define('scroll-to-btn', ScrollToBtn);
        }
    }

    function setupListeners () {
        document.querySelectorAll(".js-po-modal").forEach((modal) => {
            modal.addEventListener("click", (event) => {
                const isBackdrop = event.target === modal;
                const backdropClickClose = modal.getAttribute("data-backdrop-close") === "true";
        
                if (isBackdrop && backdropClickClose && typeof modal.close === "function") {
                    modal.close();
                }
            });
        });
        
        document.querySelectorAll(".js-po-modal-close").forEach((btn) => {
            btn.addEventListener("click", () => {
                if (btn.classList.contains("tw-po-modal-close")) {
                    document.querySelectorAll("dialog[open]").forEach((d) => d.close());
                } else {
                    btn.closest("dialog")?.close();
                }
            });
        });
        
        const PENDING_PROMO_CODE_KEY = "po_pending_subscribe_promocode";
        const PENDING_PROMO_PATH_KEY = "po_pending_subscribe_path";
        const PENDING_PROMO_TS_KEY = "po_pending_subscribe_timestamp";
        const PENDING_PROMO_MAX_AGE_MS = 2 * 60 * 60 * 1000;

        const normalizePath = (path) => {
            const cleaned = String(path || "/").replace(/\/+$/, "");
            return cleaned === "" ? "/" : cleaned;
        };

        const clearPendingSubscribePromo = () => {
            try {
                sessionStorage.removeItem(PENDING_PROMO_CODE_KEY);
                sessionStorage.removeItem(PENDING_PROMO_PATH_KEY);
                sessionStorage.removeItem(PENDING_PROMO_TS_KEY);
            } catch (error) {
                // no-op: sessionStorage can be unavailable (privacy mode)
            }
        };

        const cachePendingSubscribePromo = (btn) => {
            const modalId = btn.getAttribute("data-modal-id");
            if (modalId !== "newsletter-dialog") return;

            const promoEnabled = btn.getAttribute("data-subscribe-promocode-enabled") === "true";
            const promoValue = (btn.getAttribute("data-subscribe-promocode-value") || "").trim();

            if (!promoEnabled || promoValue === "") {
                clearPendingSubscribePromo();
                return;
            }

            try {
                sessionStorage.setItem(PENDING_PROMO_CODE_KEY, promoValue);
                sessionStorage.setItem(PENDING_PROMO_PATH_KEY, normalizePath(window.location.pathname));
                sessionStorage.setItem(PENDING_PROMO_TS_KEY, String(Date.now()));
            } catch (error) {
                // no-op: sessionStorage can be unavailable (privacy mode)
            }
        };

        const applyPendingSubscribePromoTracking = () => {
            const params = new URLSearchParams(window.location.search);
            const hasSuccessParam =
                params.get("contact_posted") === "true" ||
                params.get("customer_posted") === "true";
            const hasSuccessMessage = Boolean(
                document.querySelector(".newsletter-form__message--success, #ContactFooter-success")
            );

            if (!hasSuccessParam && !hasSuccessMessage) return;

            let promoValue = "";
            let promoPath = "/";
            let promoTimestamp = "";

            try {
                promoValue = (sessionStorage.getItem(PENDING_PROMO_CODE_KEY) || "").trim();
                promoPath = normalizePath(sessionStorage.getItem(PENDING_PROMO_PATH_KEY) || "/");
                promoTimestamp = sessionStorage.getItem(PENDING_PROMO_TS_KEY) || "";
            } catch (error) {
                promoValue = "";
            }

            if (promoValue === "") {
                clearPendingSubscribePromo();
                return;
            }

            const currentPath = normalizePath(window.location.pathname);
            if (promoPath !== currentPath) return;

            const parsedTimestamp = Number.parseInt(promoTimestamp, 10);
            if (
                Number.isNaN(parsedTimestamp) ||
                Date.now() - parsedTimestamp > PENDING_PROMO_MAX_AGE_MS
            ) {
                clearPendingSubscribePromo();
                return;
            }

            try {
                const currentUrl = new URL(window.location.href);
                currentUrl.searchParams.set("promocode", promoValue);
                window.history.replaceState({}, "", `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
            } catch (error) {
                // no-op: URL updates can fail in non-browser contexts
            }

            try {
                sessionStorage.setItem("po_promo_status", "promo added");
                sessionStorage.setItem("po_promo_added", "true");
                sessionStorage.setItem("po_promo_code", promoValue);
            } catch (error) {
                // no-op: sessionStorage can be unavailable (privacy mode)
            }

            clearPendingSubscribePromo();
        };

        const openNewsletterDialogOnFormResult = () => {
            const params = new URLSearchParams(window.location.search);
            const hasNewsletterMarker = params.get("newsletter_subscribed") === "true";
            if (!hasNewsletterMarker) return;

            const dialog = document.getElementById("newsletter-dialog");
            if (!dialog || typeof dialog.showModal !== "function") return;
            if (dialog.open) return;

            dialog.showModal();

            const successWrapper = dialog.querySelector("#NewsletterSuccessWrapper");
            if (successWrapper) {
                successWrapper.classList.remove("tw-hidden");
            }

            const focusTarget =
                dialog.querySelector(".newsletter-form__message--success") ||
                dialog.querySelector("#ContactFooter-error");
            if (focusTarget && typeof focusTarget.focus === "function") {
                focusTarget.focus({ preventScroll: true });
            }
        };

        applyPendingSubscribePromoTracking();
        openNewsletterDialogOnFormResult();

        document.querySelectorAll(".js-po-modal-btn").forEach((btn) => {
            btn.addEventListener("click", (event) => {
                event.stopPropagation();
                event.preventDefault();
                cachePendingSubscribePromo(btn);
                const modalId = btn.getAttribute("data-modal-id");
                const target = modalId ? document.getElementById(modalId) : null;

                if (target && typeof target.showModal === "function") {
                    target.showModal();
                }
            });
        });

        document.querySelectorAll(".js-po-multi-modal-btn").forEach((btn) => {
            btn.addEventListener("click", (event) => {
                event.stopPropagation();
                event.preventDefault();
                cachePendingSubscribePromo(btn);
                const modalIds = btn.getAttribute("data-multi-modal-id")?.split("|").map(id => id.trim()) || [];
                modalIds.forEach((id) => {
                    const target = id ? document.getElementById(id) : null;
                    if (!target) return;

                    if (target && typeof target.showModal === "function") {
                        target.showModal();
                    }
                });
            });
        });

        document.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
            if (checkbox.getAttribute("data-indeterminate") === "true") {
                checkbox.indeterminate = true;
            }
        });
    
        document.querySelectorAll(".js-po-draggable").forEach((elmnt) => {
            let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
          
            const dragMouseDown = (e) => {
              e.preventDefault();
    
              pos3 = e.clientX;
              pos4 = e.clientY;
    
              document.addEventListener("mouseup", closeDragElement);
              document.addEventListener("mousemove", elementDrag);
            }
          
            const elementDrag = (e) => {
              e.preventDefault();
    
              pos1 = pos3 - e.clientX;
              pos2 = pos4 - e.clientY;
              pos3 = e.clientX;
              pos4 = e.clientY;
              
              elmnt.style.top = (elmnt.offsetTop - pos2) + "px";
              elmnt.style.left = (elmnt.offsetLeft - pos1) + "px";
              elmnt.style.position = "absolute";
            }
          
            const closeDragElement = () => {
              document.removeEventListener("mouseup", closeDragElement);
              document.removeEventListener("mousemove", elementDrag);
            }
    
            const restorePos = (e) => {
                e.preventDefault();
    
                elmnt.style.removeProperty("position");
                elmnt.style.removeProperty("top");
                elmnt.style.removeProperty("left");
            }
    
            elmnt.addEventListener("mousedown", dragMouseDown);
            elmnt.addEventListener("contextmenu", restorePos);
        });
        
        document.querySelectorAll(".js-customerLogin").forEach((form) => {
            form.addEventListener("submit", async (event) => {
                const fdata = new FormData(form);
                const submitButtons = form.querySelectorAll("[type='submit']");
                const storefrontUrl = form.getAttribute("data-storefront-url");
                const storefrontToken = fdata.get("storefront_token");
    
                const input = {
                    email: fdata.get("customer[email]"),
                    password: fdata.get("customer[password]")
                }
    
                event.preventDefault();
                submitButtons.forEach(btn => btn.disabled = true);
    
                try {
                    const response = await fetch(storefrontUrl, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Shopify-Storefront-Access-Token": storefrontToken
                        },
                        body: JSON.stringify({
                            query: `#graphql
                                mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
                                    customerAccessTokenCreate(input: $input) {
                                        customerAccessToken {
                                            accessToken
                                        }
                                        customerUserErrors {
                                            message
                                        }
                                    }
                                }
                            `,
                            variables: { input }
                        })
                    });
    
                    const result = await response.json();
                    const token = result.data?.customerAccessTokenCreate?.customerAccessToken;
                    
                    if (result.error) {
                        console.error(result.error);
                    }
    
                    if (token) {
                        sessionStorage.setItem("customerAccessToken", token.accessToken);
                    }
                }
                catch (error) {
                    console.error(error);
                }
    
                form.submit();
            });
        });
    
        document.querySelectorAll(".js-showPickerLb").forEach((lb) => {
            lb.addEventListener("click", () => {
                const targetId = lb.getAttribute("for");
                const targetEl = targetId ? document.getElementById(targetId) : null;
    
                if (targetEl && targetEl.showPicker && !targetEl.disabled && !targetEl.readOnly) {
                    targetEl.showPicker();
                }
            });
        });
    }

    function setupCardSkeletonManager () {
        const CARD_SELECTOR = '.tw-po-tile[data-card-ready]';
        const watchedCards = new WeakSet();
        const fallbackTimeoutMs = 15000;

        const resolvePrimaryImage = (card) =>
            card.querySelector(
                '[data-pd-image-wrapper] img, .img-main img, .po-img-wrapper img, img'
            );

        const markCardReady = (card) => {
            if (!card || !card.isConnected || card.dataset.cardReady === "true") {
                return;
            }

            card.dataset.cardReady = "true";
        };

        const watchCardImage = (card) => {
            let imageObserver = null;
            let fallbackTimer = null;

            const cleanup = () => {
                if (imageObserver) {
                    imageObserver.disconnect();
                    imageObserver = null;
                }

                if (fallbackTimer) {
                    clearTimeout(fallbackTimer);
                    fallbackTimer = null;
                }
            };

            const observeImage = (img) => {
                if (!img) {
                    return false;
                }

                if (img.complete) {
                    markCardReady(card);
                    return true;
                }

                const onReady = () => {
                    markCardReady(card);
                    cleanup();
                };

                img.addEventListener("load", onReady, { once: true });
                img.addEventListener("error", onReady, { once: true });
                return true;
            };

            if (observeImage(resolvePrimaryImage(card))) {
                cleanup();
                return;
            }

            imageObserver = new MutationObserver(() => {
                if (card.dataset.cardReady === "true") {
                    cleanup();
                    return;
                }

                if (observeImage(resolvePrimaryImage(card))) {
                    cleanup();
                }
            });

            imageObserver.observe(card, { childList: true, subtree: true });

            fallbackTimer = window.setTimeout(() => {
                markCardReady(card);
                cleanup();
            }, fallbackTimeoutMs);
        };

        const observeCard = (card) => {
            if (!card || watchedCards.has(card) || card.dataset.cardReady !== "false") {
                return;
            }

            watchedCards.add(card);
            watchCardImage(card);
        };

        const hydrateCards = (root) => {
            if (!root) {
                return;
            }

            if (root.matches?.(CARD_SELECTOR)) {
                observeCard(root);
            }

            root.querySelectorAll?.(CARD_SELECTOR).forEach(observeCard);
        };

        hydrateCards(document);

        const rootObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== Node.ELEMENT_NODE) {
                        return;
                    }

                    hydrateCards(node);
                });
            });
        });

        if (document.body) {
            rootObserver.observe(document.body, { childList: true, subtree: true });
        }
    }
    
    setupCustomElements();
    setupListeners();
    setupCardSkeletonManager();
}

opComponents();
